// Engineering Designer — Realistic scenes + 3D-styled part art.
// Adds trade-specific canvas backdrops (bathroom for plumbing, workshop for
// carpentry, etc.) and detailed SVG parts with gradients / shadows / props
// so the lab looks like a real workspace, not a flat editor.
// Author: CognitiaIndia · Phase 40.13

import React from 'react';

// -----------------------------------------------------------------------------
// SCENES — one immersive SVG backdrop per kit. Rendered as the first layer
// inside the canvas, behind the grid.  All scenes use viewBox 100x100 and
// stretch to fill the canvas via preserveAspectRatio="none".
// -----------------------------------------------------------------------------
export const SCENES = {
  // --- Legacy plumbing kit backdrop is NO LONGER always-on.  A plain grid
  // canvas is the default so students start with a blank slate. Room-scale
  // scenes live under the "prompt scenes" section below (kitchen, bathroom,
  // garden, workshop, factory) and are triggered by the prompt bar
  // ("empty kitchen", "empty bathroom", ...) — never by the kit alone.
  // The old plumbing.  Kept as `plumbing_kitchen_full` for backward compat
  // if any external caller references it, but not exported by name.

  // -------------------------------------------------------------------------
  // PROMPT SCENES — blank rooms that students build up piece by piece via
  // the prompt bar.  "empty kitchen" clears the canvas and lays down this
  // base scene; "add cabinet" / "add sink" etc. drop furniture on top.
  // -------------------------------------------------------------------------
  kitchen: () => (
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="kchWall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fef3c7" />
          <stop offset="1" stopColor="#fde68a" />
        </linearGradient>
        <pattern id="pKchTile" width="8" height="8" patternUnits="userSpaceOnUse">
          <rect width="8" height="8" fill="url(#kchWall)" />
          <path d="M0 8 H8 M8 0 V8" stroke="#d97706" strokeWidth="0.2" opacity="0.4" />
        </pattern>
        <linearGradient id="kchFloor" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a8a29e" />
          <stop offset="1" stopColor="#57534e" />
        </linearGradient>
        <pattern id="pKchFloor" width="14" height="14" patternUnits="userSpaceOnUse">
          <rect width="14" height="14" fill="url(#kchFloor)" />
          <path d="M0 14 H14 M14 0 V14" stroke="#292524" strokeWidth="0.35" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="100" height="72" fill="url(#pKchTile)" opacity="0.85" />
      <rect x="0" y="72" width="100" height="28" fill="url(#pKchFloor)" opacity="0.85" />
      {/* baseboard */}
      <rect x="0" y="70" width="100" height="2" fill="#78350f" opacity="0.6" />
      {/* window on far wall (empty) */}
      <g opacity="0.7">
        <rect x="10" y="14" width="22" height="18" fill="#7dd3fc" stroke="#78350f" strokeWidth="0.5" />
        <line x1="21" y1="14" x2="21" y2="32" stroke="#78350f" strokeWidth="0.4" />
        <line x1="10" y1="23" x2="32" y2="23" stroke="#78350f" strokeWidth="0.4" />
      </g>
      {/* wall clock */}
      <g opacity="0.75">
        <circle cx="80" cy="18" r="4" fill="#f8fafc" stroke="#78350f" strokeWidth="0.4" />
        <line x1="80" y1="15" x2="80" y2="18" stroke="#0f172a" strokeWidth="0.4" />
        <line x1="80" y1="18" x2="83" y2="18" stroke="#0f172a" strokeWidth="0.3" />
      </g>
      {/* power outlet */}
      <g transform="translate(58 40)" opacity="0.75">
        <rect x="0" y="0" width="5" height="6" rx="0.5" fill="#f8fafc" stroke="#78350f" strokeWidth="0.2" />
        <rect x="1" y="1" width="1" height="1.5" fill="#0f172a" />
        <rect x="3" y="1" width="1" height="1.5" fill="#0f172a" />
      </g>
      <g transform="translate(4 4)" opacity="0.65">
        <rect x="0" y="0" width="22" height="5" rx="0.6" fill="#78350f" />
        <text x="1.5" y="3.6" fill="#fef3c7" fontSize="3" fontWeight="700" fontFamily="sans-serif">EMPTY KITCHEN</text>
      </g>
    </svg>
  ),
  bathroom: () => (
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="bthWall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#e0f2fe" />
          <stop offset="1" stopColor="#bae6fd" />
        </linearGradient>
        <pattern id="pBthTile" width="7" height="7" patternUnits="userSpaceOnUse">
          <rect width="7" height="7" fill="url(#bthWall)" />
          <path d="M0 7 H7 M7 0 V7" stroke="#0369a1" strokeWidth="0.15" opacity="0.55" />
        </pattern>
        <pattern id="pBthFloor" width="10" height="10" patternUnits="userSpaceOnUse">
          <rect width="10" height="10" fill="#94a3b8" />
          <path d="M0 10 H10 M10 0 V10" stroke="#475569" strokeWidth="0.3" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="100" height="72" fill="url(#pBthTile)" opacity="0.85" />
      <rect x="0" y="72" width="100" height="28" fill="url(#pBthFloor)" opacity="0.85" />
      <rect x="0" y="70" width="100" height="2" fill="#0f172a" opacity="0.35" />
      {/* mirror on wall */}
      <g opacity="0.75">
        <rect x="70" y="18" width="18" height="24" rx="1" fill="#e2e8f0" stroke="#334155" strokeWidth="0.4" />
        <path d="M72 20 L86 40" stroke="white" strokeWidth="0.6" opacity="0.85" />
      </g>
      {/* floor drain */}
      <g transform="translate(84 92)">
        <rect x="0" y="0" width="8" height="4" rx="0.4" fill="#334155" stroke="#0f172a" strokeWidth="0.3" />
        {[1, 2.5, 4, 5.5, 7].map((x, i) => (
          <rect key={i} x={x} y="0.6" width="0.5" height="2.8" fill="#0f172a" />
        ))}
      </g>
      <g transform="translate(4 4)" opacity="0.65">
        <rect x="0" y="0" width="24" height="5" rx="0.6" fill="#0369a1" />
        <text x="1.5" y="3.6" fill="#e0f2fe" fontSize="3" fontWeight="700" fontFamily="sans-serif">EMPTY BATHROOM</text>
      </g>
    </svg>
  ),
  garden: () => (
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="skyG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#7dd3fc" />
          <stop offset="1" stopColor="#dbeafe" />
        </linearGradient>
        <linearGradient id="grdG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#84cc16" />
          <stop offset="1" stopColor="#365314" />
        </linearGradient>
        <pattern id="pGrass" width="6" height="4" patternUnits="userSpaceOnUse">
          <rect width="6" height="4" fill="url(#grdG)" />
          <path d="M1 4 L1 2 M3 4 L3 1.5 M5 4 L5 2" stroke="#166534" strokeWidth="0.25" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="100" height="60" fill="url(#skyG)" opacity="0.85" />
      <rect x="0" y="60" width="100" height="40" fill="url(#pGrass)" opacity="0.9" />
      {/* Sun */}
      <circle cx="88" cy="12" r="6" fill="#fbbf24" opacity="0.9" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
        <line key={a} x1="88" y1="12" x2={88 + Math.cos(a * Math.PI / 180) * 10} y2={12 + Math.sin(a * Math.PI / 180) * 10} stroke="#fbbf24" strokeWidth="0.4" opacity="0.7" />
      ))}
      {/* Distant hills */}
      <path d="M0 60 Q 18 48 36 58 T 72 55 T 100 60 L 100 62 L 0 62 Z" fill="#166534" opacity="0.55" />
      {/* Fence line */}
      <g opacity="0.7">
        {[8, 20, 32, 44, 56, 68, 80, 92].map((x, i) => (
          <rect key={i} x={x} y="55" width="1" height="10" fill="#a16207" />
        ))}
        <rect x="6" y="57" width="90" height="0.8" fill="#a16207" />
        <rect x="6" y="61" width="90" height="0.8" fill="#a16207" />
      </g>
      <g transform="translate(4 4)" opacity="0.65">
        <rect x="0" y="0" width="18" height="5" rx="0.6" fill="#166534" />
        <text x="1.5" y="3.6" fill="#dcfce7" fontSize="3" fontWeight="700" fontFamily="sans-serif">EMPTY GARDEN</text>
      </g>
    </svg>
  ),
  workshop: () => (
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="wshWall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#334155" />
          <stop offset="1" stopColor="#1e293b" />
        </linearGradient>
        <pattern id="pWshBrick" width="12" height="6" patternUnits="userSpaceOnUse">
          <rect width="12" height="6" fill="url(#wshWall)" />
          <rect x="0" y="0" width="6" height="3" fill="#475569" opacity="0.6" />
          <rect x="6" y="3" width="6" height="3" fill="#475569" opacity="0.6" />
          <path d="M0 3 H12 M6 0 V3 M0 6 V3 M6 3 V6" stroke="#0f172a" strokeWidth="0.25" />
        </pattern>
        <linearGradient id="wshFlr" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#57534e" />
          <stop offset="1" stopColor="#292524" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="100" height="72" fill="url(#pWshBrick)" opacity="0.85" />
      <rect x="0" y="72" width="100" height="28" fill="url(#wshFlr)" opacity="0.85" />
      {/* Overhead lights */}
      {[20, 50, 80].map((x, i) => (
        <g key={i}>
          <line x1={x} y1="0" x2={x} y2="8" stroke="#0f172a" strokeWidth="0.4" />
          <rect x={x - 5} y="8" width="10" height="2" fill="#facc15" opacity="0.8" />
          <ellipse cx={x} cy="12" rx="12" ry="4" fill="#fde68a" opacity="0.3" />
        </g>
      ))}
      <g transform="translate(4 26)" opacity="0.65">
        <rect x="0" y="0" width="22" height="5" rx="0.6" fill="#0f172a" />
        <text x="1.5" y="3.6" fill="#facc15" fontSize="3" fontWeight="700" fontFamily="sans-serif">EMPTY WORKSHOP</text>
      </g>
    </svg>
  ),
  factory: () => (
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="facSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#475569" />
          <stop offset="1" stopColor="#1e293b" />
        </linearGradient>
        <linearGradient id="facFlr" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#78716c" />
          <stop offset="1" stopColor="#292524" />
        </linearGradient>
        <pattern id="pFacGrid" width="10" height="10" patternUnits="userSpaceOnUse">
          <rect width="10" height="10" fill="url(#facFlr)" />
          <path d="M0 10 H10 M10 0 V10" stroke="#0f172a" strokeWidth="0.35" opacity="0.6" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="100" height="65" fill="url(#facSky)" />
      <rect x="0" y="65" width="100" height="35" fill="url(#pFacGrid)" opacity="0.85" />
      {/* Overhead crane rail */}
      <rect x="0" y="10" width="100" height="1.6" fill="#f59e0b" opacity="0.75" />
      {/* I-beam ceiling structure */}
      <g stroke="#57534e" strokeWidth="0.4" fill="none" opacity="0.5">
        <line x1="0" y1="5" x2="100" y2="5" />
        <line x1="20" y1="5" x2="20" y2="15" />
        <line x1="50" y1="5" x2="50" y2="15" />
        <line x1="80" y1="5" x2="80" y2="15" />
      </g>
      <g transform="translate(4 20)" opacity="0.65">
        <rect x="0" y="0" width="22" height="5" rx="0.6" fill="#f59e0b" />
        <text x="1.5" y="3.6" fill="#292524" fontSize="3" fontWeight="700" fontFamily="sans-serif">EMPTY FACTORY</text>
      </g>
    </svg>
  ),
  electrical: () => (
    // Electronics bench with breadboard grid + power outlet
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="benchWall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0b1220" />
          <stop offset="1" stopColor="#111827" />
        </linearGradient>
        <linearGradient id="benchTop" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1e293b" />
          <stop offset="1" stopColor="#334155" />
        </linearGradient>
        <pattern id="pBread" width="4" height="4" patternUnits="userSpaceOnUse">
          <rect width="4" height="4" fill="url(#benchTop)" />
          <circle cx="2" cy="2" r="0.4" fill="#eab308" opacity="0.35" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="100" height="60" fill="url(#benchWall)" />
      <rect x="0" y="60" width="100" height="40" fill="url(#pBread)" opacity="0.7" />
      {/* wall socket */}
      <g transform="translate(84 20)">
        <rect x="0" y="0" width="10" height="14" rx="1.5" fill="#e2e8f0" />
        <circle cx="3.4" cy="6" r="0.9" fill="#0f172a" />
        <circle cx="6.6" cy="6" r="0.9" fill="#0f172a" />
        <rect x="4.4" y="9.5" width="1.2" height="2" fill="#0f172a" />
      </g>
      {/* voltage meter */}
      <g transform="translate(6 12)">
        <rect x="0" y="0" width="20" height="12" rx="1.5" fill="#0f172a" stroke="#f59e0b" strokeWidth="0.4" />
        <rect x="1" y="2" width="18" height="4" fill="#0a0a0a" />
        <text x="2" y="5.2" fill="#facc15" fontSize="3" fontFamily="monospace">12.4 V</text>
        <circle cx="4" cy="9" r="1" fill="#dc2626" />
        <circle cx="8" cy="9" r="1" fill="#22c55e" />
      </g>
    </svg>
  ),
  robotics: () => (
    // Robotics lab — anti-static mat + tools rail
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="labWall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1e1b4b" />
          <stop offset="1" stopColor="#312e81" />
        </linearGradient>
        <linearGradient id="labMat" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0f172a" />
          <stop offset="1" stopColor="#1f2937" />
        </linearGradient>
        <pattern id="pMat" width="6" height="6" patternUnits="userSpaceOnUse">
          <rect width="6" height="6" fill="url(#labMat)" />
          <path d="M0 6 H6 M6 0 V6" stroke="#a855f7" strokeWidth="0.15" opacity="0.5" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="100" height="55" fill="url(#labWall)" />
      <rect x="0" y="55" width="100" height="45" fill="url(#pMat)" />
      {/* tool rail */}
      <rect x="0" y="52" width="100" height="1" fill="#a855f7" opacity="0.5" />
      {[10,25,40,55,70,85].map((x, i) => (
        <g key={i} transform={`translate(${x} 40)`}>
          <line x1="0" y1="0" x2="0" y2="12" stroke="#c084fc" strokeWidth="0.3" opacity="0.6" />
          {i % 2 === 0 ? (
            <path d="M-2 6 H2 M0 6 V10" stroke="#c084fc" strokeWidth="0.6" opacity="0.7" />
          ) : (
            <circle cx="0" cy="8" r="1.5" fill="none" stroke="#c084fc" strokeWidth="0.4" opacity="0.7" />
          )}
        </g>
      ))}
    </svg>
  ),
  mining: () => (
    // Underground mine — rock walls, wooden support beams, headlamp glow
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <radialGradient id="mineGlow" cx="0.5" cy="0.3" r="0.7">
          <stop offset="0" stopColor="#fbbf24" stopOpacity="0.25" />
          <stop offset="1" stopColor="#78350f" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="rockG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3f2f1a" />
          <stop offset="1" stopColor="#1c1206" />
        </linearGradient>
        <pattern id="pRock" width="16" height="12" patternUnits="userSpaceOnUse">
          <rect width="16" height="12" fill="url(#rockG)" />
          <path d="M0 3 Q4 0 8 3 T16 3 M0 8 Q4 5 8 8 T16 8" stroke="#78350f" strokeWidth="0.4" fill="none" opacity="0.6" />
          <circle cx="3" cy="2" r="0.5" fill="#57534e" />
          <circle cx="12" cy="9" r="0.6" fill="#57534e" />
          <circle cx="9" cy="5" r="0.4" fill="#57534e" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="100" height="100" fill="url(#pRock)" opacity="0.9" />
      <rect x="0" y="0" width="100" height="100" fill="url(#mineGlow)" />
      {/* support beams */}
      {[15, 50, 85].map((x, i) => (
        <g key={i}>
          <rect x={x} y="5" width="3" height="90" fill="#78350f" opacity="0.75" />
          <rect x={x - 1} y="5" width="5" height="3" fill="#57342b" opacity="0.8" />
        </g>
      ))}
      <rect x="15" y="2" width="73" height="2.6" fill="#78350f" opacity="0.7" />
      {/* rails on floor */}
      <rect x="0" y="93" width="100" height="0.6" fill="#57534e" />
      <rect x="0" y="96" width="100" height="0.6" fill="#57534e" />
      {[5, 20, 35, 50, 65, 80, 95].map((x, i) => (
        <rect key={i} x={x} y="92.6" width="3" height="4.3" fill="#3f2f1a" />
      ))}
    </svg>
  ),
  metallurgy: () => (
    // Realistic continuous-casting foundry — ladle silhouette, tundish
    // buffer, water-cooled mold, support rollers, safety cut zone.
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <radialGradient id="furnaceGlow" cx="0.5" cy="0.35" r="0.55">
          <stop offset="0" stopColor="#fb923c" stopOpacity="0.35" />
          <stop offset="1" stopColor="#111" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="fdyWall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0c0a09" />
          <stop offset="1" stopColor="#1c1917" />
        </linearGradient>
        <pattern id="pBrick" width="12" height="6" patternUnits="userSpaceOnUse">
          <rect width="12" height="6" fill="#7c2d12" />
          <rect x="0" y="0" width="6" height="3" fill="#9a3412" />
          <rect x="6" y="3" width="6" height="3" fill="#9a3412" />
          <path d="M0 3 H12 M6 0 V3 M0 6 V3 M6 3 V6" stroke="#3f1503" strokeWidth="0.3" />
        </pattern>
        <linearGradient id="sand" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#57534e" />
          <stop offset="1" stopColor="#292524" />
        </linearGradient>
        <linearGradient id="molten" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fef3c7" />
          <stop offset="0.4" stopColor="#f97316" />
          <stop offset="1" stopColor="#7c2d12" />
        </linearGradient>
        <linearGradient id="steelC" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#e5e7eb" />
          <stop offset="1" stopColor="#374151" />
        </linearGradient>
      </defs>
      {/* Brick wall + sand floor */}
      <rect x="0" y="0" width="100" height="72" fill="url(#fdyWall)" />
      <rect x="0" y="0" width="100" height="45" fill="url(#pBrick)" opacity="0.45" />
      <rect x="0" y="72" width="100" height="28" fill="url(#sand)" />

      {/* Overhead crane rail */}
      <rect x="0" y="6" width="100" height="1.4" fill="#334155" />
      <rect x="0" y="7.4" width="100" height="0.5" fill="#0f172a" opacity="0.7" />
      <g opacity="0.7">
        <rect x="18" y="7.9" width="8" height="4" fill="#f59e0b" stroke="#0f172a" strokeWidth="0.3" />
        <line x1="22" y1="11.9" x2="22" y2="18" stroke="#0f172a" strokeWidth="0.4" />
      </g>

      {/* LADLE (top-left) tilted, pouring molten */}
      <g transform="translate(14 12) rotate(-18)">
        <path d="M0 6 L14 6 L13 16 Q7 20 1 16 Z" fill="#475569" stroke="#0f172a" strokeWidth="0.4" />
        <ellipse cx="7" cy="6" rx="7" ry="1.5" fill="#78716c" />
        <ellipse cx="7" cy="6" rx="5.5" ry="1" fill="url(#molten)" />
        {/* pour spout */}
        <path d="M13 8 L20 12 L22 18" stroke="url(#molten)" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.85" />
      </g>
      <text x="10" y="4" fill="#fbbf24" fontSize="2.4" fontWeight="700" fontFamily="sans-serif" opacity="0.75">MOLTEN LADLE 1600°C</text>

      {/* TUNDISH — receiving buffer */}
      <g transform="translate(30 22)">
        <path d="M0 4 L16 4 L14 12 L2 12 Z" fill="#475569" stroke="#0f172a" strokeWidth="0.5" />
        <ellipse cx="8" cy="4" rx="8" ry="1.4" fill="#f97316" />
        <ellipse cx="8" cy="4" rx="6.5" ry="0.9" fill="url(#molten)" />
        {/* drop to mold */}
        <rect x="7" y="11" width="2" height="6" fill="url(#molten)" opacity="0.9" />
      </g>
      <text x="40" y="21.4" fill="#fbbf24" fontSize="2" opacity="0.7">TUNDISH · 1550°C</text>

      {/* WATER-COOLED MOLD */}
      <g transform="translate(35 39)">
        <rect x="0" y="0" width="14" height="10" fill="#0369a1" stroke="#0f172a" strokeWidth="0.5" rx="0.5" />
        <rect x="1" y="1" width="12" height="8" fill="url(#molten)" opacity="0.9" />
        {/* cooling water pipes on sides */}
        <rect x="-3" y="2" width="3" height="1.2" fill="#7dd3fc" />
        <rect x="-3" y="6" width="3" height="1.2" fill="#7dd3fc" />
        <rect x="14" y="2" width="3" height="1.2" fill="#7dd3fc" />
        <rect x="14" y="6" width="3" height="1.2" fill="#7dd3fc" />
      </g>
      <text x="52" y="42" fill="#7dd3fc" fontSize="2" opacity="0.7">WATER-COOLED MOLD</text>

      {/* SUPPORT ROLLERS + solidifying strand */}
      <path d="M42 49 Q55 54 68 62" stroke="url(#molten)" strokeWidth="4" fill="none" strokeLinecap="round" opacity="0.85" />
      <path d="M42 49 Q55 54 68 62" stroke="url(#steelC)" strokeWidth="2.4" fill="none" strokeLinecap="round" opacity="0.5" />
      {[[47,52],[53,54],[59,57],[65,60]].map(([x,y], i) => (
        <g key={i}>
          <circle cx={x} cy={y+1.4} r="1.4" fill="#57534e" stroke="#0f172a" strokeWidth="0.3" />
          <circle cx={x} cy={y+1.4} r="0.5" fill="#94a3b8" />
        </g>
      ))}

      {/* Safe-cut zone */}
      <g transform="translate(70 60)">
        <rect x="0" y="0" width="10" height="4" fill="#57534e" stroke="#0f172a" strokeWidth="0.3" />
        <rect x="0" y="0" width="10" height="0.8" fill="#facc15" />
        <path d="M5 -1 L5 4" stroke="#dc2626" strokeWidth="0.5" strokeDasharray="0.4 0.4" />
      </g>
      <text x="72" y="59" fill="#fca5a5" fontSize="1.8" opacity="0.7">SAFE-CUT</text>

      {/* Cut billets on floor */}
      <g transform="translate(82 74)">
        {[[0,0],[3,0],[6,0],[1.5,3],[4.5,3]].map(([x,y], i) => (
          <rect key={i} x={x} y={y} width="2.5" height="2.5" fill="url(#steelC)" stroke="#0f172a" strokeWidth="0.3" />
        ))}
      </g>

      {/* Smokestacks + smoke */}
      <rect x="6" y="1" width="4" height="9" fill="#0f172a" />
      <ellipse cx="8" cy="1" rx="2.5" ry="0.9" fill="#78716c" />
      <rect x="88" y="1" width="4" height="9" fill="#0f172a" />
      <ellipse cx="90" cy="1" rx="2.5" ry="0.9" fill="#78716c" />

      {/* Ember glow overlay */}
      <rect x="0" y="0" width="100" height="100" fill="url(#furnaceGlow)" />
      {/* Sparks */}
      {[[46,50,0.5],[54,55,0.35],[62,58,0.6],[70,62,0.45]].map(([x,y,r], i) => (
        <circle key={i} cx={x} cy={y} r={r} fill="#fbbf24" opacity="0.9" />
      ))}
    </svg>
  ),
  civil: () => (
    // Realistic construction job-site — steel-frame skeleton, tower crane
    // lifting a beam, concrete truck at right, safety barrier tape.
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="civSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#7dd3fc" />
          <stop offset="0.7" stopColor="#fef3c7" />
          <stop offset="1" stopColor="#fde68a" />
        </linearGradient>
        <linearGradient id="civGround" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a8a29e" />
          <stop offset="1" stopColor="#44403c" />
        </linearGradient>
        <pattern id="pGravel" width="6" height="6" patternUnits="userSpaceOnUse">
          <rect width="6" height="6" fill="url(#civGround)" />
          <circle cx="1.5" cy="2" r="0.4" fill="#292524" opacity="0.6" />
          <circle cx="4" cy="4" r="0.5" fill="#1c1917" opacity="0.7" />
          <circle cx="2.5" cy="5" r="0.3" fill="#44403c" opacity="0.8" />
        </pattern>
        <pattern id="pHazard" width="4" height="1.4" patternUnits="userSpaceOnUse" patternTransform="rotate(0)">
          <rect width="2" height="1.4" fill="#fbbf24" />
          <rect x="2" width="2" height="1.4" fill="#0f172a" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="100" height="72" fill="url(#civSky)" opacity="0.7" />
      <rect x="0" y="72" width="100" height="28" fill="url(#pGravel)" opacity="0.9" />
      {/* Distant city silhouette */}
      <g opacity="0.28" fill="#334155">
        <rect x="0" y="52" width="8" height="20" />
        <rect x="9" y="46" width="6" height="26" />
        <rect x="16" y="56" width="5" height="16" />
        <rect x="22" y="42" width="7" height="30" />
      </g>
      {/* Tower crane */}
      <g stroke="#f59e0b" strokeWidth="0.55" fill="none" opacity="0.9">
        <rect x="72" y="4" width="3" height="66" fill="#f59e0b" opacity="0.85" />
        {[10, 18, 26, 34, 42, 50, 58, 66].map((y, i) => (
          <g key={i}>
            <line x1="72" y1={y} x2="75" y2={y + 4} />
            <line x1="75" y1={y} x2="72" y2={y + 4} />
          </g>
        ))}
        <line x1="60" y1="10" x2="94" y2="10" stroke="#f59e0b" strokeWidth="0.9" />
        <line x1="70" y1="6" x2="90" y2="10" stroke="#f59e0b" strokeWidth="0.5" />
        <line x1="68" y1="10" x2="72" y2="4" stroke="#f59e0b" strokeWidth="0.5" />
      </g>
      {/* Trolley + hook lifting a beam */}
      <g>
        <rect x="82" y="10" width="4" height="1.8" fill="#0f172a" />
        <line x1="84" y1="11.8" x2="84" y2="28" stroke="#0f172a" strokeWidth="0.3" />
        <rect x="76" y="28" width="16" height="2.2" fill="#94a3b8" stroke="#0f172a" strokeWidth="0.3" />
      </g>
      {/* Concrete mixer truck */}
      <g transform="translate(4 66)">
        <rect x="0" y="0" width="16" height="6" rx="0.6" fill="#dc2626" stroke="#0f172a" strokeWidth="0.3" />
        <ellipse cx="12" cy="3" rx="7" ry="3.5" fill="#78716c" stroke="#0f172a" strokeWidth="0.3" />
        <path d="M6 1 Q12 2 18 1 M6 5 Q12 4 18 5" stroke="#292524" strokeWidth="0.3" fill="none" />
        <circle cx="3" cy="6.5" r="1.4" fill="#0f172a" />
        <circle cx="8" cy="6.5" r="1.4" fill="#0f172a" />
        <circle cx="16" cy="6.5" r="1.4" fill="#0f172a" />
      </g>
      {/* Steel skeleton frame (background structure) */}
      <g opacity="0.35" stroke="#57534e" strokeWidth="0.5" fill="none">
        <rect x="30" y="34" width="30" height="38" />
        <path d="M30 46 H60 M30 58 H60 M45 34 V72" />
        <path d="M30 34 L60 46 M30 46 L60 58 M30 58 L60 72" />
      </g>
      {/* Hazard tape */}
      <rect x="0" y="70" width="100" height="1.4" fill="url(#pHazard)" opacity="0.85" />
      {/* Traffic cones on ground */}
      {[10, 26, 42, 82].map((x, i) => (
        <g key={i} transform={`translate(${x} 88)`}>
          <path d="M0 6 L3 0 L6 6 Z" fill="#f97316" stroke="#0f172a" strokeWidth="0.2" />
          <rect x="1" y="2.5" width="4" height="0.6" fill="white" />
          <rect x="-0.5" y="6" width="7" height="0.8" fill="#0f172a" />
        </g>
      ))}
      {/* Blueprint label */}
      <g transform="translate(4 4)" opacity="0.6">
        <rect x="0" y="0" width="22" height="5" rx="0.6" fill="#0f172a" />
        <text x="1.5" y="3.6" fill="#fbbf24" fontSize="3" fontWeight="700" fontFamily="sans-serif">SITE PLAN</text>
      </g>
    </svg>
  ),
  carpentry: () => (
    // Realistic carpentry workshop — pegboard with hanging tools, workbench
    // with vise, wood plank stacks and sawdust floor.
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="woodP" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a16207" />
          <stop offset="1" stopColor="#78350f" />
        </linearGradient>
        <pattern id="pWoodWall" width="20" height="10" patternUnits="userSpaceOnUse">
          <rect width="20" height="10" fill="url(#woodP)" />
          <path d="M0 0 H20 M0 10 H20" stroke="#3f1503" strokeWidth="0.4" />
          <path d="M2 5 Q6 3 10 5 T18 5" stroke="#78350f" strokeWidth="0.3" fill="none" opacity="0.6" />
        </pattern>
        <linearGradient id="sawdust" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#d4a373" />
          <stop offset="1" stopColor="#7c2d12" />
        </linearGradient>
        <linearGradient id="peg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#c2410c" />
          <stop offset="1" stopColor="#7c2d12" />
        </linearGradient>
        <linearGradient id="benchT" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a16207" />
          <stop offset="1" stopColor="#3f1503" />
        </linearGradient>
      </defs>
      {/* Wood plank wall */}
      <rect x="0" y="0" width="100" height="50" fill="url(#pWoodWall)" opacity="0.75" />
      {/* Pegboard */}
      <g transform="translate(58 5)">
        <rect x="0" y="0" width="38" height="30" rx="0.6" fill="url(#peg)" stroke="#3f1503" strokeWidth="0.4" />
        <g fill="#3f1503" opacity="0.65">
          {[3,7,11,15,19,23,27,31,35].map((x) => [3,7,11,15,19,23,27].map((y) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="0.35" />
          )))}
        </g>
        {/* Hammer */}
        <g transform="translate(3 2)">
          <rect x="0" y="4" width="0.8" height="9" fill="#78350f" />
          <rect x="-1.5" y="2" width="4" height="3" fill="#334155" stroke="#0f172a" strokeWidth="0.2" />
        </g>
        {/* Handsaw */}
        <g transform="translate(9 4)">
          <rect x="0" y="0" width="12" height="1.6" fill="#e2e8f0" stroke="#64748b" strokeWidth="0.2" />
          <path d="M0 1.6 L1 3 L2 1.6 L3 3 L4 1.6 L5 3 L6 1.6 L7 3 L8 1.6 L9 3 L10 1.6 L11 3 L12 1.6" fill="#94a3b8" />
          <rect x="12" y="-0.5" width="4" height="4" rx="0.6" fill="#dc2626" />
        </g>
        {/* Wrench */}
        <g transform="translate(24 3)">
          <rect x="0" y="0" width="8" height="1" fill="#94a3b8" />
          <circle cx="0" cy="0.5" r="1.5" fill="none" stroke="#94a3b8" strokeWidth="0.5" />
          <circle cx="8" cy="0.5" r="1.5" fill="none" stroke="#94a3b8" strokeWidth="0.5" />
        </g>
        {/* Drill silhouette */}
        <g transform="translate(3 15)">
          <rect x="0" y="0" width="6" height="4" rx="0.6" fill="#0f172a" />
          <rect x="6" y="1" width="4" height="2" fill="#334155" />
          <rect x="1" y="4" width="3" height="4" fill="#0f172a" />
        </g>
        {/* Screwdriver set */}
        {[16, 20, 24].map((x, i) => (
          <g key={i} transform={`translate(${x} 14)`}>
            <rect x="0" y="0" width="1.2" height="4" fill={['#dc2626','#facc15','#3b82f6'][i]} />
            <rect x="0.35" y="4" width="0.5" height="7" fill="#94a3b8" />
          </g>
        ))}
      </g>
      {/* Workbench */}
      <rect x="4" y="50" width="52" height="4" fill="url(#benchT)" stroke="#3f1503" strokeWidth="0.35" />
      <rect x="6" y="54" width="3" height="10" fill="#78350f" />
      <rect x="51" y="54" width="3" height="10" fill="#78350f" />
      {/* Vise */}
      <g transform="translate(38 45.5)">
        <rect x="0" y="0" width="12" height="5" fill="#334155" stroke="#0f172a" strokeWidth="0.3" />
        <rect x="2" y="-2" width="2" height="4" fill="#94a3b8" />
        <rect x="8" y="-2" width="2" height="4" fill="#94a3b8" />
        <circle cx="6" cy="2.5" r="1" fill="#f59e0b" />
      </g>
      {/* Sawdust floor */}
      <rect x="0" y="64" width="100" height="36" fill="url(#sawdust)" opacity="0.7" />
      {/* Wood plank stack right */}
      <g transform="translate(76 68)">
        {[0, 3, 6, 9].map((y, i) => (
          <rect key={i} x="0" y={y} width="20" height="2.5" rx="0.3" fill={i % 2 ? '#b45309' : '#a16207'} stroke="#3f1503" strokeWidth="0.25" />
        ))}
      </g>
      {/* Sawdust particles */}
      {[[6,74],[18,80],[30,72],[44,84],[56,76],[68,82],[16,90]].map(([x,y], i) => (
        <circle key={i} cx={x} cy={y} r="0.35" fill="#78350f" opacity="0.7" />
      ))}
      {/* Blueprint label */}
      <g transform="translate(4 4)" opacity="0.6">
        <rect x="0" y="0" width="26" height="5" rx="0.6" fill="#0f172a" />
        <text x="1.5" y="3.6" fill="#fbbf24" fontSize="3" fontWeight="700" fontFamily="sans-serif">CARPENTRY BENCH</text>
      </g>
    </svg>
  ),
  welding: () => (
    // Realistic welding bay — steel-plate wall, fume-extractor hood, arc
    // flash, gas cylinders, worktable with slag scars, floor sparks.
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="steelWall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1c1917" />
          <stop offset="1" stopColor="#292524" />
        </linearGradient>
        <pattern id="pDiamond" width="12" height="12" patternUnits="userSpaceOnUse">
          <rect width="12" height="12" fill="url(#steelWall)" />
          <path d="M6 1 L11 6 L6 11 L1 6 Z" stroke="#57534e" strokeWidth="0.4" fill="none" opacity="0.7" />
          <path d="M6 3 L9 6 L6 9 L3 6 Z" fill="#44403c" opacity="0.5" />
        </pattern>
        <radialGradient id="arcGlow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#fef3c7" stopOpacity="0.85" />
          <stop offset="0.4" stopColor="#f97316" stopOpacity="0.45" />
          <stop offset="1" stopColor="#0f172a" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cylBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#dc2626" />
          <stop offset="1" stopColor="#7f1d1d" />
        </linearGradient>
        <linearGradient id="cylBody2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0284c7" />
          <stop offset="1" stopColor="#0c4a6e" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="100" height="72" fill="url(#pDiamond)" />
      <rect x="0" y="72" width="100" height="28" fill="#0c0a09" />
      {/* Fume-extractor hood */}
      <g transform="translate(30 4)">
        <rect x="0" y="0" width="40" height="4" fill="#374151" stroke="#0f172a" strokeWidth="0.3" />
        <path d="M0 4 L8 12 L32 12 L40 4 Z" fill="#57534e" stroke="#0f172a" strokeWidth="0.3" />
        <rect x="16" y="12" width="8" height="6" fill="#0f172a" />
        {/* Fume line */}
        <path d="M20 18 Q22 22 20 26 Q18 30 20 34" stroke="#94a3b8" strokeWidth="0.6" fill="none" opacity="0.5" />
      </g>
      {/* Gas cylinders on right */}
      {[80, 88].map((x, i) => (
        <g key={i} transform={`translate(${x} 46)`}>
          <rect x="0" y="4" width="6" height="22" rx="1.2" fill={i === 0 ? 'url(#cylBody)' : 'url(#cylBody2)'} stroke="#0f172a" strokeWidth="0.35" />
          <rect x="2" y="0" width="2" height="4" fill="#525252" />
          <rect x="1" y="4" width="4" height="1" fill="#334155" />
          <circle cx="3" cy="1" r="0.8" fill="#facc15" />
          <text x="1" y="16" fill="white" fontSize="1.6" fontWeight="700">{i === 0 ? 'O₂' : 'Ar'}</text>
        </g>
      ))}
      {/* Welder mask on wall */}
      <g transform="translate(10 8)" opacity="0.7">
        <rect x="0" y="0" width="10" height="12" rx="1" fill="#0f172a" stroke="#57534e" strokeWidth="0.3" />
        <rect x="1.5" y="3" width="7" height="4" fill="#166534" stroke="#22c55e" strokeWidth="0.2" />
        <path d="M2 12 Q5 15 8 12" stroke="#57534e" strokeWidth="0.4" fill="none" />
      </g>
      {/* Welder silhouette */}
      <g transform="translate(38 42)" opacity="0.9">
        <ellipse cx="10" cy="28" rx="12" ry="1.6" fill="#000" opacity="0.5" />
        <rect x="4" y="8" width="12" height="18" rx="2" fill="#0f172a" />
        <circle cx="10" cy="4" r="4" fill="#0f172a" stroke="#374151" strokeWidth="0.3" />
        <rect x="7" y="3" width="6" height="2.4" fill="#166534" stroke="#22c55e" strokeWidth="0.2" />
        {/* torch in hand */}
        <rect x="16" y="14" width="8" height="1.8" fill="#0f172a" transform="rotate(-25 20 14.9)" />
        <circle cx="22" cy="9" r="0.6" fill="#fbbf24" />
      </g>
      {/* Worktable */}
      <rect x="4" y="66" width="92" height="4" fill="#57534e" stroke="#0f172a" strokeWidth="0.35" />
      <rect x="6" y="70" width="3" height="10" fill="#334155" />
      <rect x="91" y="70" width="3" height="10" fill="#334155" />
      {/* Slag scars on table */}
      {[15, 24, 40, 52, 66].map((x, i) => (
        <ellipse key={i} cx={x} cy="67.8" rx="1.4" ry="0.5" fill="#000" opacity="0.6" />
      ))}
      {/* Arc glow at weld */}
      <circle cx="52" cy="66" r="16" fill="url(#arcGlow)" />
      {/* Sparks flying */}
      {[[45,72],[58,70],[50,80],[62,84],[38,78],[70,74],[45,88]].map(([x,y], i) => (
        <g key={i}>
          <line x1={x} y1={y} x2={x + (i % 2 ? 2.5 : -2.5)} y2={y + (i % 2 ? -2.5 : 2.5)} stroke="#fef3c7" strokeWidth="0.35" opacity="0.85" />
          <circle cx={x} cy={y} r="0.4" fill="#fbbf24" opacity="0.9" />
        </g>
      ))}
      {/* Blueprint label */}
      <g transform="translate(4 26)" opacity="0.6">
        <rect x="0" y="0" width="22" height="5" rx="0.6" fill="#0f172a" />
        <text x="1.5" y="3.6" fill="#fbbf24" fontSize="3" fontWeight="700" fontFamily="sans-serif">WELDING BAY</text>
      </g>
    </svg>
  ),
  fitters: () => (
    // Machine shop bench — greasy metal + tool outlines
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="fitWall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1e293b" />
          <stop offset="1" stopColor="#0f172a" />
        </linearGradient>
        <linearGradient id="fitBench" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#475569" />
          <stop offset="1" stopColor="#334155" />
        </linearGradient>
        <pattern id="pBenchGrid" width="10" height="10" patternUnits="userSpaceOnUse">
          <rect width="10" height="10" fill="url(#fitBench)" />
          <circle cx="1" cy="1" r="0.4" fill="#0f172a" />
          <circle cx="9" cy="1" r="0.4" fill="#0f172a" />
          <circle cx="1" cy="9" r="0.4" fill="#0f172a" />
          <circle cx="9" cy="9" r="0.4" fill="#0f172a" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="100" height="55" fill="url(#fitWall)" />
      <rect x="0" y="55" width="100" height="45" fill="url(#pBenchGrid)" opacity="0.85" />
      {/* pegboard */}
      <g opacity="0.45" stroke="#94a3b8" strokeWidth="0.3" fill="none">
        <rect x="8" y="10" width="30" height="20" />
        {/* wrenches */}
        <rect x="12" y="14" width="6" height="1" />
        <rect x="12" y="18" width="8" height="1" />
        <rect x="12" y="22" width="10" height="1" />
        {/* torque */}
        <rect x="24" y="14" width="10" height="1.4" />
        <circle cx="24" cy="14.7" r="1.6" fill="none" />
      </g>
      {/* vise silhouette */}
      <g opacity="0.35" fill="#94a3b8">
        <rect x="72" y="40" width="18" height="10" />
        <rect x="76" y="34" width="2" height="8" />
        <rect x="84" y="34" width="2" height="8" />
      </g>
    </svg>
  ),
};

// -----------------------------------------------------------------------------
// PART_ART — 3D-styled SVG artwork per part base type.  Each entry is a
// function that receives {w, h, running, colour} and returns a <g> that fits
// inside a 0..w × 0..h viewBox.  Rendered inside CanvasPart.
// -----------------------------------------------------------------------------
const grad = (id, from, to, vert = true) => (
  <linearGradient id={id} x1="0" y1="0" x2={vert ? 0 : 1} y2={vert ? 1 : 0}>
    <stop offset="0" stopColor={from} />
    <stop offset="1" stopColor={to} />
  </linearGradient>
);

export const PART_ART = {
  // --- PLUMBING -------------------------------------------------------------
  tank: ({ w, h, running }) => (
    <g>
      <defs>{grad('tankG', '#60a5fa', '#1e3a8a')}</defs>
      <rect x="1" y="6" width={w - 2} height={h - 8} rx="4" fill="url(#tankG)" stroke="#0f172a" strokeWidth="0.8" />
      <rect x="4" y="4" width={w - 8} height="4" rx="1.5" fill="#334155" />
      <rect x={w / 2 - 3} y="1" width="6" height="4" rx="1" fill="#475569" />
      {/* water level */}
      <rect x="3" y={h - h * 0.55} width={w - 6} height={h * 0.5} fill="#38bdf8" opacity={running ? 0.9 : 0.55}>
        {running && <animate attributeName="y" values={`${h - h * 0.5};${h - h * 0.6};${h - h * 0.5}`} dur="2.2s" repeatCount="indefinite" />}
      </rect>
      <rect x="4" y="7" width="3" height={h - 12} fill="white" opacity="0.25" rx="1" />
    </g>
  ),
  pipe_h: ({ w, h }) => (
    <g>
      <defs>{grad('pipeH', '#7dd3fc', '#0369a1')}</defs>
      <rect x="0" y="0" width={w} height={h} rx={h / 2} fill="url(#pipeH)" stroke="#0f172a" strokeWidth="0.6" />
      <rect x="0" y="1" width={w} height={h * 0.35} rx={h / 2} fill="white" opacity="0.35" />
      <rect x="-1" y="0" width="4" height={h} fill="#334155" rx="1" />
      <rect x={w - 3} y="0" width="4" height={h} fill="#334155" rx="1" />
    </g>
  ),
  pipe_v: ({ w, h }) => (
    <g>
      <defs>{grad('pipeV', '#7dd3fc', '#0369a1', false)}</defs>
      <rect x="0" y="0" width={w} height={h} rx={w / 2} fill="url(#pipeV)" stroke="#0f172a" strokeWidth="0.6" />
      <rect x="1" y="0" width={w * 0.35} height={h} rx={w / 2} fill="white" opacity="0.35" />
      <rect x="0" y="-1" width={w} height="4" fill="#334155" rx="1" />
      <rect x="0" y={h - 3} width={w} height="4" fill="#334155" rx="1" />
    </g>
  ),
  elbow: ({ w, h }) => (
    <g>
      <defs>{grad('elbG', '#38bdf8', '#075985')}</defs>
      <path d={`M0 ${h / 2} Q0 0 ${w / 2} 0 L${w} 0 L${w} ${h * 0.4} Q${w * 0.4} ${h * 0.4} ${w * 0.4} ${h} L0 ${h} Z`} fill="url(#elbG)" stroke="#0f172a" strokeWidth="0.6" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 4} fill="#0369a1" opacity="0.4" />
    </g>
  ),
  tee: ({ w, h }) => (
    <g>
      <defs>{grad('teeG', '#38bdf8', '#075985')}</defs>
      <rect x="0" y={h * 0.35} width={w} height={h * 0.3} fill="url(#teeG)" stroke="#0f172a" strokeWidth="0.6" rx="2" />
      <rect x={w * 0.35} y="0" width={w * 0.3} height={h} fill="url(#teeG)" stroke="#0f172a" strokeWidth="0.6" rx="2" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 6} fill="#075985" />
    </g>
  ),
  valve: ({ w, h, running }) => (
    <g>
      <defs>{grad('valveB', '#f87171', '#7f1d1d')}</defs>
      <rect x="0" y={h * 0.35} width={w} height={h * 0.3} fill="#475569" rx="1" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) * 0.42} fill="url(#valveB)" stroke="#0f172a" strokeWidth="0.8" />
      <rect x={w / 2 - 1} y="0" width="2" height={h * 0.3} fill="#334155" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) * 0.15} fill="#fecaca" />
      {running && <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) * 0.42} fill="none" stroke="#fbbf24" strokeWidth="0.6" opacity="0.7"><animate attributeName="r" values={`${Math.min(w,h)*0.42};${Math.min(w,h)*0.52};${Math.min(w,h)*0.42}`} dur="1.5s" repeatCount="indefinite" /></circle>}
    </g>
  ),
  tap: ({ w, h }) => (
    <g>
      <defs>{grad('tapG', '#e2e8f0', '#64748b')}</defs>
      <rect x={w * 0.15} y={h * 0.55} width={w * 0.7} height={h * 0.35} fill="url(#tapG)" rx="2" stroke="#0f172a" strokeWidth="0.4" />
      <path d={`M${w * 0.5} ${h * 0.55} Q${w * 0.5} ${h * 0.2} ${w * 0.15} ${h * 0.2}`} stroke="url(#tapG)" strokeWidth={h * 0.15} fill="none" strokeLinecap="round" />
      <circle cx={w * 0.5} cy={h * 0.12} r={h * 0.1} fill="#dc2626" stroke="#0f172a" strokeWidth="0.3" />
      <rect x={w * 0.15} y={h * 0.9} width={w * 0.15} height="2" fill="#0284c7" />
    </g>
  ),
  pump: ({ w, h, running }) => (
    <g>
      <defs>
        {grad('pumpG', '#34d399', '#064e3b')}
      </defs>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="url(#pumpG)" stroke="#0f172a" strokeWidth="0.8" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 4} fill="#052e2b" />
      <g transform={`translate(${w / 2} ${h / 2})`}>
        {[0, 60, 120, 180, 240, 300].map((a) => (
          <rect key={a} x="-1" y={-Math.min(w, h) / 4} width="2" height={Math.min(w, h) / 3} fill="#a7f3d0" transform={`rotate(${a})`} />
        ))}
        {running && <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="1s" repeatCount="indefinite" additive="sum" />}
      </g>
    </g>
  ),
  drain: ({ w, h }) => (
    <g>
      <defs>{grad('drainG', '#475569', '#0f172a')}</defs>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="url(#drainG)" stroke="#0f172a" strokeWidth="0.6" />
      {[0, 45, 90, 135].map((a) => (
        <rect key={a} x={w / 2 - Math.min(w, h) * 0.35} y={h / 2 - 0.5} width={Math.min(w, h) * 0.7} height="1" fill="#000" transform={`rotate(${a} ${w / 2} ${h / 2})`} opacity="0.7" />
      ))}
    </g>
  ),
  shower: ({ w, h, running }) => (
    <g>
      <defs>{grad('shwG', '#7dd3fc', '#0369a1')}</defs>
      <rect x={w * 0.2} y="0" width={w * 0.6} height={h * 0.25} rx="2" fill="url(#shwG)" stroke="#0f172a" strokeWidth="0.4" />
      <rect x={w * 0.42} y={h * 0.25} width={w * 0.16} height={h * 0.15} fill="#64748b" />
      <ellipse cx={w / 2} cy={h * 0.45} rx={w * 0.42} ry={h * 0.1} fill="#e2e8f0" stroke="#0f172a" strokeWidth="0.4" />
      {/* nozzle dots */}
      {[0.15, 0.3, 0.45, 0.6, 0.75, 0.85].map((f, i) => (
        <circle key={i} cx={w * f} cy={h * 0.48} r="0.7" fill="#0f172a" />
      ))}
      {running && [0.2, 0.4, 0.6, 0.8].map((f, i) => (
        <line key={i} x1={w * f} y1={h * 0.5} x2={w * f} y2={h}
          stroke="#38bdf8" strokeWidth="0.8" opacity="0.75" strokeLinecap="round">
          <animate attributeName="y1" values={`${h*0.5};${h*0.75};${h*0.5}`} dur="0.6s" begin={`${i*0.1}s`} repeatCount="indefinite" />
          <animate attributeName="y2" values={`${h};${h*1.05};${h}`} dur="0.6s" begin={`${i*0.1}s`} repeatCount="indefinite" />
        </line>
      ))}
    </g>
  ),

  // --- ELECTRICAL -----------------------------------------------------------
  battery: ({ w, h, running }) => (
    <g>
      <defs>{grad('batG', '#fde68a', '#a16207')}</defs>
      <rect x="1" y="1" width={w - 4} height={h - 2} rx="2" fill="url(#batG)" stroke="#0f172a" strokeWidth="0.6" />
      <rect x={w - 3} y={h * 0.3} width="3" height={h * 0.4} fill="#334155" rx="1" />
      <text x={w * 0.15} y={h * 0.65} fill="#0f172a" fontSize={h * 0.5} fontWeight="900">−</text>
      <text x={w * 0.7} y={h * 0.65} fill="#0f172a" fontSize={h * 0.5} fontWeight="900">+</text>
      <rect x="1" y="1" width={w - 4} height={h * 0.25} rx="2" fill="white" opacity="0.35" />
      {running && <rect x="1" y="1" width={w - 4} height={h - 2} rx="2" fill="none" stroke="#facc15" strokeWidth="0.6" opacity="0.7"><animate attributeName="opacity" values="0.4;1;0.4" dur="0.9s" repeatCount="indefinite" /></rect>}
    </g>
  ),
  led: ({ w, h, running }) => (
    <g>
      <defs>
        <radialGradient id="ledG" cx="0.5" cy="0.4" r="0.6">
          <stop offset="0" stopColor={running ? '#fef3c7' : '#fef3c7'} />
          <stop offset="0.6" stopColor={running ? '#f59e0b' : '#f59e0b'} />
          <stop offset="1" stopColor={running ? '#7c2d12' : '#78350f'} />
        </radialGradient>
      </defs>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="url(#ledG)" stroke="#0f172a" strokeWidth="0.6" />
      <ellipse cx={w * 0.35} cy={h * 0.3} rx={w * 0.18} ry={h * 0.12} fill="white" opacity="0.6" />
      <rect x={w * 0.42} y={h - 3} width="1" height="4" fill="#334155" />
      <rect x={w * 0.55} y={h - 3} width="1" height="4" fill="#334155" />
      {running && <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) * 0.9} fill="#fde68a" opacity="0.35"><animate attributeName="opacity" values="0.15;0.5;0.15" dur="1.1s" repeatCount="indefinite" /></circle>}
    </g>
  ),
  resistor: ({ w, h }) => (
    <g>
      <rect x="0" y={h * 0.4} width={w} height={h * 0.2} fill="#334155" />
      <rect x={w * 0.15} y="2" width={w * 0.7} height={h - 4} rx={h * 0.4} fill="#a3671e" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.25} y="3" width="2" height={h - 6} fill="#7f1d1d" />
      <rect x={w * 0.4} y="3" width="2" height={h - 6} fill="#000" />
      <rect x={w * 0.55} y="3" width="2" height={h - 6} fill="#f59e0b" />
      <rect x={w * 0.7} y="3" width="2" height={h - 6} fill="#facc15" />
    </g>
  ),
  switch: ({ w, h, running }) => (
    <g>
      <rect x="0" y={h * 0.35} width={w} height={h * 0.3} fill="#57534e" />
      <rect x={w * 0.1} y={h * 0.15} width={w * 0.8} height={h * 0.7} rx="2" fill="#334155" stroke="#0f172a" strokeWidth="0.5" />
      <circle cx={w * 0.25} cy={h / 2} r={h * 0.14} fill="#0f172a" />
      <circle cx={w * 0.75} cy={h / 2} r={h * 0.14} fill="#0f172a" />
      <rect x={w * 0.24} y={h * 0.28} width={w * (running ? 0.52 : 0.18)} height={h * 0.14} rx="1.5" fill={running ? '#fbbf24' : '#94a3b8'} stroke="#0f172a" strokeWidth="0.3" />
    </g>
  ),
  motor: ({ w, h, running }) => (
    <g>
      <defs>{grad('motG', '#a78bfa', '#4c1d95')}</defs>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="url(#motG)" stroke="#0f172a" strokeWidth="0.7" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 3} fill="#312e81" />
      <g transform={`translate(${w / 2} ${h / 2})`}>
        {[0, 90, 180, 270].map((a) => (
          <rect key={a} x="-0.8" y={-Math.min(w, h) / 3.2} width="1.6" height={Math.min(w, h) / 2} fill="#e0e7ff" transform={`rotate(${a})`} />
        ))}
        {running && <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="0.7s" repeatCount="indefinite" additive="sum" />}
      </g>
      <circle cx={w / 2} cy={h / 2} r="1.5" fill="#0f172a" />
    </g>
  ),
  capacitor: ({ w, h }) => (
    <g>
      <rect x="0" y={h * 0.4} width={w * 0.3} height={h * 0.2} fill="#334155" />
      <rect x={w * 0.7} y={h * 0.4} width={w * 0.3} height={h * 0.2} fill="#334155" />
      <rect x={w * 0.3} y="2" width={w * 0.15} height={h - 4} fill="#3b82f6" stroke="#0f172a" strokeWidth="0.4" />
      <rect x={w * 0.55} y="2" width={w * 0.15} height={h - 4} fill="#3b82f6" stroke="#0f172a" strokeWidth="0.4" />
      <text x={w * 0.35} y={h * 0.7} fill="white" fontSize={h * 0.45} fontWeight="700">+</text>
      <text x={w * 0.6} y={h * 0.7} fill="white" fontSize={h * 0.45} fontWeight="700">−</text>
    </g>
  ),
  wire: ({ w, h }) => (
    <g>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="#0f172a" stroke="#94a3b8" strokeWidth="0.5" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 4} fill="#facc15" />
    </g>
  ),
  buzzer: ({ w, h, running }) => (
    <g>
      <defs>{grad('bzG', '#fb923c', '#7c2d12')}</defs>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="url(#bzG)" stroke="#0f172a" strokeWidth="0.6" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 3} fill="#7c2d12" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 6} fill="#0f172a" />
      {running && [1, 1.4, 1.8].map((s, i) => (
        <circle key={i} cx={w / 2} cy={h / 2} r={Math.min(w, h) / 3} fill="none" stroke="#fbbf24" strokeWidth="0.5" opacity="0">
          <animate attributeName="r" values={`${Math.min(w,h)/3};${Math.min(w,h)*s}`} dur="1.2s" begin={`${i*0.4}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.9;0" dur="1.2s" begin={`${i*0.4}s`} repeatCount="indefinite" />
        </circle>
      ))}
    </g>
  ),
  ground: ({ w, h }) => (
    <g>
      <rect x={w * 0.4} y="0" width={w * 0.2} height={h * 0.4} fill="#525252" />
      <rect x="0" y={h * 0.4} width={w} height="2" fill="#525252" />
      <rect x={w * 0.15} y={h * 0.6} width={w * 0.7} height="2" fill="#525252" />
      <rect x={w * 0.3} y={h * 0.8} width={w * 0.4} height="2" fill="#525252" />
    </g>
  ),
  sensor: ({ w, h, running }) => (
    <g>
      <defs><radialGradient id="senG" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stopColor="#fef9c3" /><stop offset="1" stopColor="#a16207" /></radialGradient></defs>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="url(#senG)" stroke="#0f172a" strokeWidth="0.6" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 3} fill="#0f172a" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 5} fill="#facc15" />
      {running && <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) * 0.7} fill="none" stroke="#facc15" opacity="0.5" strokeWidth="0.4"><animate attributeName="r" values={`${Math.min(w,h)*0.5};${Math.min(w,h)*0.9};${Math.min(w,h)*0.5}`} dur="1.4s" repeatCount="indefinite" /></circle>}
    </g>
  ),

  // --- ROBOTICS -------------------------------------------------------------
  chassis: ({ w, h }) => (
    <g>
      <defs>{grad('chsG', '#a78bfa', '#4c1d95')}</defs>
      <rect x="1" y="3" width={w - 2} height={h - 6} rx="4" fill="url(#chsG)" stroke="#0f172a" strokeWidth="0.7" />
      <rect x="3" y="5" width={w - 6} height={h - 10} rx="3" fill="none" stroke="#e0e7ff" strokeWidth="0.3" opacity="0.5" />
      {[0.2, 0.5, 0.8].map((f, i) => (
        <circle key={i} cx={w * f} cy={h / 2} r="1.5" fill="#0f172a" />
      ))}
    </g>
  ),
  wheel: ({ w, h, running }) => (
    <g>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="#0f172a" stroke="#1e293b" strokeWidth="0.8" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 3} fill="#57534e" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 6} fill="#94a3b8" />
      <g transform={`translate(${w / 2} ${h / 2})`}>
        {[0, 60, 120, 180, 240, 300].map((a) => (
          <rect key={a} x="-0.5" y={-Math.min(w, h) / 2 + 2} width="1" height={Math.min(w, h) / 3} fill="#e2e8f0" transform={`rotate(${a})`} opacity="0.7" />
        ))}
        {running && <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="0.8s" repeatCount="indefinite" additive="sum" />}
      </g>
    </g>
  ),
  motor2: ({ w, h, running }) => PART_ART.motor({ w, h, running }),
  sensor2: ({ w, h, running }) => (
    <g>
      <defs>{grad('snr', '#34d399', '#065f46')}</defs>
      <rect x="1" y="1" width={w - 2} height={h - 2} rx="3" fill="url(#snr)" stroke="#0f172a" strokeWidth="0.6" />
      <circle cx={w * 0.3} cy={h * 0.4} r={Math.min(w, h) * 0.15} fill="#0f172a" />
      <circle cx={w * 0.7} cy={h * 0.4} r={Math.min(w, h) * 0.15} fill="#0f172a" />
      <rect x={w * 0.2} y={h * 0.7} width={w * 0.6} height="1.5" fill="#0f172a" opacity="0.5" />
      {running && <g><circle cx={w * 0.3} cy={h * 0.4} r="0.6" fill="#fbbf24"><animate attributeName="r" values="0.3;1.2;0.3" dur="0.9s" repeatCount="indefinite" /></circle><circle cx={w * 0.7} cy={h * 0.4} r="0.6" fill="#fbbf24"><animate attributeName="r" values="0.3;1.2;0.3" dur="0.9s" begin="0.3s" repeatCount="indefinite" /></circle></g>}
    </g>
  ),
  arm: ({ w, h }) => (
    <g>
      <defs>{grad('armG', '#c084fc', '#581c87', false)}</defs>
      <rect x={w * 0.15} y="1" width={w * 0.7} height={h - 2} rx="3" fill="url(#armG)" stroke="#0f172a" strokeWidth="0.6" />
      <circle cx={w / 2} cy={h * 0.15} r={w * 0.3} fill="#4c1d95" stroke="#0f172a" strokeWidth="0.5" />
      <circle cx={w / 2} cy={h * 0.5} r={w * 0.25} fill="#4c1d95" stroke="#0f172a" strokeWidth="0.5" />
      <circle cx={w / 2} cy={h * 0.85} r={w * 0.3} fill="#4c1d95" stroke="#0f172a" strokeWidth="0.5" />
      <circle cx={w / 2} cy={h * 0.15} r="1" fill="#facc15" />
      <circle cx={w / 2} cy={h * 0.5} r="1" fill="#facc15" />
    </g>
  ),
  gripper: ({ w, h }) => (
    <g>
      <rect x={w * 0.3} y="0" width={w * 0.4} height={h * 0.3} fill="#7c3aed" stroke="#0f172a" strokeWidth="0.4" />
      <path d={`M0 ${h * 0.3} L${w * 0.3} ${h * 0.3} L${w * 0.3} ${h} L${w * 0.15} ${h} Z`} fill="#d946ef" stroke="#0f172a" strokeWidth="0.4" />
      <path d={`M${w} ${h * 0.3} L${w * 0.7} ${h * 0.3} L${w * 0.7} ${h} L${w * 0.85} ${h} Z`} fill="#d946ef" stroke="#0f172a" strokeWidth="0.4" />
      <circle cx={w * 0.4} cy={h * 0.35} r="1.5" fill="#0f172a" />
      <circle cx={w * 0.6} cy={h * 0.35} r="1.5" fill="#0f172a" />
    </g>
  ),
  camera: ({ w, h, running }) => (
    <g>
      <rect x="1" y="4" width={w - 2} height={h - 5} rx="2" fill="#1f2937" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.3} y="1" width={w * 0.4} height="4" rx="1" fill="#111827" />
      <circle cx={w / 2} cy={h * 0.55} r={h * 0.28} fill="#000" stroke="#374151" strokeWidth="1" />
      <circle cx={w / 2} cy={h * 0.55} r={h * 0.18} fill="#1e40af" />
      <circle cx={w * 0.45} cy={h * 0.45} r={h * 0.06} fill="white" opacity="0.7" />
      {running && <circle cx={w * 0.85} cy={h * 0.85} r="1.5" fill="#ef4444"><animate attributeName="opacity" values="0.3;1;0.3" dur="0.8s" repeatCount="indefinite" /></circle>}
    </g>
  ),
  mcu: ({ w, h, running }) => (
    <g>
      <rect x="1" y="1" width={w - 2} height={h - 2} rx="1.5" fill="#166534" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.25} y={h * 0.25} width={w * 0.5} height={h * 0.5} fill="#0a0a0a" stroke="#22c55e" strokeWidth="0.4" />
      {[0.15, 0.3, 0.45, 0.6, 0.75].map((f, i) => (
        <g key={i}>
          <rect x={w * f - 0.5} y="0" width="1" height={h * 0.2} fill="#94a3b8" />
          <rect x={w * f - 0.5} y={h - h * 0.2} width="1" height={h * 0.2} fill="#94a3b8" />
        </g>
      ))}
      <text x={w * 0.35} y={h * 0.6} fill="#22c55e" fontSize={h * 0.3} fontFamily="monospace">MCU</text>
      {running && <circle cx={w * 0.85} cy={h * 0.85} r="1" fill="#22c55e"><animate attributeName="opacity" values="0.3;1;0.3" dur="0.8s" repeatCount="indefinite" /></circle>}
    </g>
  ),
  battery2: ({ w, h, running }) => PART_ART.battery({ w, h, running }),
  led2: ({ w, h, running }) => PART_ART.led({ w, h, running }),

  // --- MINING ---------------------------------------------------------------
  ore_gold: ({ w, h }) => (
    <g>
      <defs>{grad('oreGold', '#fde047', '#a16207')}</defs>
      <path d={`M${w * 0.1} ${h * 0.5} L${w * 0.25} ${h * 0.15} L${w * 0.6} ${h * 0.1} L${w * 0.9} ${h * 0.45} L${w * 0.85} ${h * 0.85} L${w * 0.4} ${h * 0.9} Z`} fill="url(#oreGold)" stroke="#0f172a" strokeWidth="0.6" />
      <circle cx={w * 0.35} cy={h * 0.4} r={w * 0.06} fill="#fef3c7" opacity="0.8" />
      <circle cx={w * 0.65} cy={h * 0.55} r={w * 0.05} fill="#fef3c7" opacity="0.7" />
      <circle cx={w * 0.5} cy={h * 0.75} r={w * 0.04} fill="#fef3c7" opacity="0.6" />
    </g>
  ),
  ore_iron: ({ w, h }) => (
    <g>
      <defs>{grad('oreIron', '#a8a29e', '#292524')}</defs>
      <path d={`M${w * 0.1} ${h * 0.5} L${w * 0.3} ${h * 0.1} L${w * 0.7} ${h * 0.15} L${w * 0.9} ${h * 0.5} L${w * 0.8} ${h * 0.9} L${w * 0.3} ${h * 0.85} Z`} fill="url(#oreIron)" stroke="#0f172a" strokeWidth="0.6" />
      <circle cx={w * 0.4} cy={h * 0.4} r={w * 0.05} fill="#57534e" />
      <circle cx={w * 0.65} cy={h * 0.6} r={w * 0.06} fill="#57534e" />
    </g>
  ),
  ore_coal: ({ w, h }) => (
    <g>
      <defs>{grad('oreCoal', '#0f172a', '#000')}</defs>
      <path d={`M${w * 0.1} ${h * 0.5} L${w * 0.3} ${h * 0.1} L${w * 0.7} ${h * 0.15} L${w * 0.9} ${h * 0.5} L${w * 0.8} ${h * 0.9} L${w * 0.3} ${h * 0.85} Z`} fill="url(#oreCoal)" stroke="#334155" strokeWidth="0.5" />
      <circle cx={w * 0.4} cy={h * 0.4} r={w * 0.06} fill="#1c1917" />
      <circle cx={w * 0.6} cy={h * 0.6} r={w * 0.07} fill="#1c1917" />
      <circle cx={w * 0.5} cy={h * 0.3} r="1" fill="white" opacity="0.4" />
    </g>
  ),
  drill: ({ w, h, running }) => (
    <g>
      <defs>{grad('drlG', '#f87171', '#7f1d1d')}</defs>
      <rect x="0" y={h * 0.25} width={w * 0.65} height={h * 0.5} rx="3" fill="url(#drlG)" stroke="#0f172a" strokeWidth="0.6" />
      <rect x={w * 0.55} y={h * 0.4} width={w * 0.15} height={h * 0.2} fill="#334155" />
      <path d={`M${w * 0.7} ${h * 0.5} L${w} ${h * 0.35} L${w * 0.98} ${h * 0.65} Z`} fill="#94a3b8" stroke="#0f172a" strokeWidth="0.5" />
      {[0.72, 0.8, 0.88].map((f, i) => (
        <line key={i} x1={w * f} y1={h * 0.35} x2={w * f} y2={h * 0.65} stroke="#0f172a" strokeWidth="0.4" />
      ))}
      {running && (
        <g transform={`translate(${w * 0.85} ${h / 2})`}>
          <circle r="1" fill="#fde68a" opacity="0.7">
            <animate attributeName="r" values="0.5;2;0.5" dur="0.5s" repeatCount="indefinite" />
          </circle>
        </g>
      )}
    </g>
  ),
  excavator: ({ w, h }) => (
    <g>
      <defs>{grad('excG', '#fb923c', '#7c2d12')}</defs>
      <rect x="0" y={h * 0.55} width={w * 0.8} height={h * 0.3} rx="2" fill="url(#excG)" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.15} y={h * 0.3} width={w * 0.35} height={h * 0.3} rx="2" fill="url(#excG)" stroke="#0f172a" strokeWidth="0.5" />
      <path d={`M${w * 0.5} ${h * 0.45} L${w * 0.9} ${h * 0.2} L${w * 0.95} ${h * 0.4}`} stroke="#7c2d12" strokeWidth={h * 0.08} fill="none" strokeLinecap="round" />
      <path d={`M${w * 0.88} ${h * 0.35} L${w} ${h * 0.5} L${w * 0.98} ${h * 0.7} L${w * 0.82} ${h * 0.6} Z`} fill="#57534e" stroke="#0f172a" strokeWidth="0.5" />
      <circle cx={w * 0.15} cy={h * 0.9} r={h * 0.15} fill="#0f172a" />
      <circle cx={w * 0.45} cy={h * 0.9} r={h * 0.15} fill="#0f172a" />
      <circle cx={w * 0.75} cy={h * 0.9} r={h * 0.15} fill="#0f172a" />
    </g>
  ),
  truck: ({ w, h }) => (
    <g>
      <defs>{grad('trkG', '#a3e635', '#365314')}</defs>
      <rect x={w * 0.25} y={h * 0.15} width={w * 0.7} height={h * 0.5} rx="2" fill="url(#trkG)" stroke="#0f172a" strokeWidth="0.5" />
      <rect x="0" y={h * 0.3} width={w * 0.3} height={h * 0.4} rx="2" fill="#365314" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.05} y={h * 0.35} width={w * 0.2} height={h * 0.2} fill="#7dd3fc" opacity="0.7" />
      <rect x={w * 0.3} y={h * 0.2} width={w * 0.6} height={h * 0.15} fill="#292524" opacity="0.5" />
      <circle cx={w * 0.2} cy={h * 0.85} r={h * 0.14} fill="#0f172a" />
      <circle cx={w * 0.5} cy={h * 0.85} r={h * 0.14} fill="#0f172a" />
      <circle cx={w * 0.7} cy={h * 0.85} r={h * 0.14} fill="#0f172a" />
      <circle cx={w * 0.85} cy={h * 0.85} r={h * 0.14} fill="#0f172a" />
    </g>
  ),
  conveyor: ({ w, h, running }) => (
    <g>
      <rect x="0" y={h * 0.2} width={w} height={h * 0.6} fill="#57534e" stroke="#0f172a" strokeWidth="0.5" />
      <rect x="0" y={h * 0.2} width={w} height="1.5" fill="#292524" />
      <rect x="0" y={h * 0.75} width={w} height="1.5" fill="#292524" />
      {[0.1, 0.3, 0.5, 0.7, 0.9].map((f, i) => (
        <circle key={i} cx={w * f} cy={h / 2} r={h * 0.15} fill="#0f172a" stroke="#94a3b8" strokeWidth="0.3">
          {running && <animateTransform attributeName="transform" type="rotate" from={`0 ${w * f} ${h / 2}`} to={`360 ${w * f} ${h / 2}`} dur="0.7s" repeatCount="indefinite" />}
        </circle>
      ))}
      {running && [0.15, 0.35, 0.55, 0.75].map((f, i) => (
        <circle key={i} cx={w * f} cy={h * 0.15} r="1.5" fill="#a3671e">
          <animate attributeName="cx" from="0" to={w} dur="2s" begin={`${i * 0.5}s`} repeatCount="indefinite" />
        </circle>
      ))}
    </g>
  ),
  crusher: ({ w, h, running }) => (
    <g>
      <defs>{grad('crshG', '#a3671e', '#3f1503')}</defs>
      <rect x="1" y="1" width={w - 2} height={h - 2} rx="2" fill="url(#crshG)" stroke="#0f172a" strokeWidth="0.6" />
      <path d={`M${w * 0.2} ${h * 0.35} L${w * 0.35} ${h * 0.55} L${w * 0.25} ${h * 0.75} L${w * 0.4} ${h * 0.65} L${w * 0.5} ${h * 0.85} L${w * 0.6} ${h * 0.65} L${w * 0.75} ${h * 0.75} L${w * 0.65} ${h * 0.55} L${w * 0.8} ${h * 0.35}`} stroke="#292524" strokeWidth={h * 0.06} fill="none" strokeLinecap="round" />
      <rect x={w * 0.15} y={h * 0.15} width={w * 0.7} height={h * 0.15} fill="#0f172a" />
      {running && <rect x="1" y="1" width={w - 2} height={h - 2} rx="2" fill="none" stroke="#fbbf24" strokeWidth="0.6" opacity="0.5"><animate attributeName="opacity" values="0.2;0.8;0.2" dur="0.6s" repeatCount="indefinite" /></rect>}
    </g>
  ),
  helmet: ({ w, h }) => (
    <g>
      <defs>{grad('hlmG', '#facc15', '#a16207')}</defs>
      <path d={`M${w * 0.1} ${h * 0.6} Q${w * 0.5} 0 ${w * 0.9} ${h * 0.6} L${w * 0.9} ${h * 0.85} L${w * 0.1} ${h * 0.85} Z`} fill="url(#hlmG)" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.4} y={h * 0.15} width={w * 0.2} height={h * 0.15} fill="#0f172a" />
      <ellipse cx={w * 0.5} cy={h * 0.9} rx={w * 0.45} ry={h * 0.05} fill="#0f172a" opacity="0.3" />
    </g>
  ),
  shaft: ({ w, h }) => (
    <g>
      <rect x="0" y="0" width={w} height={h} fill="#0f172a" stroke="#57342b" strokeWidth="0.8" />
      <rect x="1" y="1" width={w - 2} height="3" fill="#78350f" />
      <rect x="1" y={h - 4} width={w - 2} height="3" fill="#78350f" />
      <line x1={w / 2} y1="0" x2={w / 2} y2={h} stroke="#292524" strokeWidth="0.5" strokeDasharray="2 3" />
      {[0.15, 0.3, 0.45, 0.6, 0.75, 0.9].map((f, i) => (
        <rect key={i} x="2" y={h * f - 0.5} width={w - 4} height="1" fill="#292524" opacity="0.6" />
      ))}
    </g>
  ),

  // --- METALLURGY -----------------------------------------------------------
  furnace: ({ w, h, running }) => (
    <g>
      <defs>
        {grad('furnG', '#7c2d12', '#1c0d05')}
        <radialGradient id="furnFire" cx="0.5" cy="0.6" r="0.5">
          <stop offset="0" stopColor="#fef3c7" />
          <stop offset="0.5" stopColor="#f97316" />
          <stop offset="1" stopColor="#7c2d12" stopOpacity="0.6" />
        </radialGradient>
      </defs>
      <rect x="1" y={h * 0.15} width={w - 2} height={h * 0.85} rx="3" fill="url(#furnG)" stroke="#0f172a" strokeWidth="0.7" />
      <rect x={w * 0.15} y="1" width={w * 0.7} height={h * 0.2} rx="1" fill="#292524" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.35} y="0" width={w * 0.3} height={h * 0.15} fill="#0f172a" />
      <ellipse cx={w / 2} cy={h * 0.55} rx={w * 0.35} ry={h * 0.22} fill="url(#furnFire)">
        {running && <animate attributeName="ry" values={`${h*0.2};${h*0.28};${h*0.2}`} dur="1.2s" repeatCount="indefinite" />}
      </ellipse>
      <rect x={w * 0.4} y={h * 0.8} width={w * 0.2} height={h * 0.15} fill="#0f172a" />
      {/* smoke */}
      {running && [0.2, 0.5, 0.8].map((f, i) => (
        <circle key={i} cx={w * f} cy="0" r="1" fill="#94a3b8" opacity="0.5">
          <animate attributeName="cy" values="0;-6" dur="1.5s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;0" dur="1.5s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
        </circle>
      ))}
    </g>
  ),
  ladle: ({ w, h, running }) => (
    <g>
      <defs>{grad('ldlG', '#f97316', '#7c2d12')}</defs>
      <path d={`M${w * 0.15} ${h * 0.4} L${w * 0.15} ${h * 0.75} Q${w / 2} ${h} ${w * 0.85} ${h * 0.75} L${w * 0.85} ${h * 0.4} Z`} fill="url(#ldlG)" stroke="#0f172a" strokeWidth="0.6" />
      <ellipse cx={w / 2} cy={h * 0.4} rx={w * 0.35} ry={h * 0.1} fill="#fbbf24" />
      <rect x={w * 0.42} y="0" width={w * 0.16} height={h * 0.4} fill="#334155" />
      <rect x={w * 0.35} y="0" width={w * 0.3} height={h * 0.08} fill="#57534e" rx="1" />
      {running && (
        <ellipse cx={w / 2} cy={h * 0.4} rx={w * 0.3} ry={h * 0.06} fill="#fef3c7" opacity="0.85">
          <animate attributeName="opacity" values="0.6;1;0.6" dur="0.9s" repeatCount="indefinite" />
        </ellipse>
      )}
    </g>
  ),
  ingot: ({ w, h }) => (
    <g>
      <defs>{grad('ingG', '#d6d3d1', '#57534e')}</defs>
      <path d={`M${w * 0.05} ${h * 0.2} L${w * 0.15} ${h * 0.05} L${w * 0.85} ${h * 0.05} L${w * 0.95} ${h * 0.2} L${w * 0.95} ${h * 0.9} L${w * 0.05} ${h * 0.9} Z`} fill="url(#ingG)" stroke="#0f172a" strokeWidth="0.6" />
      <rect x={w * 0.1} y={h * 0.15} width={w * 0.8} height={h * 0.15} fill="white" opacity="0.5" />
      <text x={w * 0.35} y={h * 0.65} fill="#0f172a" fontSize={h * 0.35} fontWeight="900" fontFamily="monospace">Fe</text>
    </g>
  ),
  roller: ({ w, h, running }) => (
    <g>
      <rect x="1" y="1" width={w - 2} height={h - 2} rx="2" fill="#334155" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.1} y={h * 0.2} width={w * 0.8} height={h * 0.25} rx={h * 0.12} fill="#78716c" stroke="#0f172a" strokeWidth="0.4" />
      <rect x={w * 0.1} y={h * 0.55} width={w * 0.8} height={h * 0.25} rx={h * 0.12} fill="#78716c" stroke="#0f172a" strokeWidth="0.4" />
      {running && <>
        <circle cx={w * 0.15} cy={h * 0.325} r="1" fill="#facc15"><animate attributeName="cx" values={`${w*0.15};${w*0.85};${w*0.15}`} dur="1.2s" repeatCount="indefinite" /></circle>
        <circle cx={w * 0.15} cy={h * 0.675} r="1" fill="#facc15"><animate attributeName="cx" values={`${w*0.15};${w*0.85};${w*0.15}`} dur="1.2s" repeatCount="indefinite" /></circle>
      </>}
    </g>
  ),
  quench: ({ w, h, running }) => (
    <g>
      <defs>{grad('qchG', '#0ea5e9', '#0c4a6e')}</defs>
      <rect x="0" y={h * 0.2} width={w} height={h * 0.8} rx="2" fill="url(#qchG)" stroke="#0f172a" strokeWidth="0.6" />
      <rect x="0" y={h * 0.2} width={w} height="2" fill="#38bdf8" />
      <path d={`M0 ${h * 0.35} Q${w * 0.25} ${h * 0.25} ${w * 0.5} ${h * 0.35} T${w} ${h * 0.35}`} stroke="white" strokeWidth="0.5" fill="none" opacity="0.7">
        {running && <animate attributeName="d" values={`M0 ${h*0.35} Q${w*0.25} ${h*0.25} ${w*0.5} ${h*0.35} T${w} ${h*0.35};M0 ${h*0.35} Q${w*0.25} ${h*0.45} ${w*0.5} ${h*0.35} T${w} ${h*0.35};M0 ${h*0.35} Q${w*0.25} ${h*0.25} ${w*0.5} ${h*0.35} T${w} ${h*0.35}`} dur="1.6s" repeatCount="indefinite" />}
      </path>
      {running && [0.2, 0.5, 0.8].map((f, i) => (
        <circle key={i} cx={w * f} cy={h * 0.7} r="1.2" fill="white" opacity="0.7">
          <animate attributeName="cy" values={`${h*0.9};${h*0.35}`} dur="1.4s" begin={`${i*0.3}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.8;0" dur="1.4s" begin={`${i*0.3}s`} repeatCount="indefinite" />
        </circle>
      ))}
    </g>
  ),
  alloy_brass: ({ w, h }) => (
    <g>
      <defs><radialGradient id="brsG" cx="0.4" cy="0.4" r="0.6"><stop offset="0" stopColor="#fde047" /><stop offset="1" stopColor="#78350f" /></radialGradient></defs>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="url(#brsG)" stroke="#0f172a" strokeWidth="0.6" />
      <text x={w * 0.25} y={h * 0.62} fill="#0f172a" fontSize={h * 0.35} fontWeight="900">Br</text>
    </g>
  ),
  alloy_bronze: ({ w, h }) => (
    <g>
      <defs><radialGradient id="bnzG" cx="0.4" cy="0.4" r="0.6"><stop offset="0" stopColor="#f97316" /><stop offset="1" stopColor="#7c2d12" /></radialGradient></defs>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="url(#bnzG)" stroke="#0f172a" strokeWidth="0.6" />
      <text x={w * 0.2} y={h * 0.62} fill="#0f172a" fontSize={h * 0.35} fontWeight="900">Bz</text>
    </g>
  ),
  anvil: ({ w, h }) => (
    <g>
      <defs>{grad('anvG', '#1f2937', '#000')}</defs>
      <path d={`M${w * 0.1} ${h * 0.15} L${w * 0.9} ${h * 0.15} L${w * 0.85} ${h * 0.35} L${w * 0.6} ${h * 0.35} L${w * 0.6} ${h * 0.85} L${w * 0.85} ${h} L${w * 0.15} ${h} L${w * 0.4} ${h * 0.85} L${w * 0.4} ${h * 0.35} L${w * 0.15} ${h * 0.35} Z`} fill="url(#anvG)" stroke="#334155" strokeWidth="0.6" />
      <rect x={w * 0.1} y={h * 0.15} width={w * 0.8} height="1.5" fill="#e2e8f0" opacity="0.5" />
    </g>
  ),
  crucible: ({ w, h, running }) => (
    <g>
      <defs>{grad('crcG', '#a3671e', '#3f1503')}</defs>
      <path d={`M${w * 0.15} ${h * 0.2} Q${w / 2} ${h * 0.05} ${w * 0.85} ${h * 0.2} L${w * 0.8} ${h * 0.95} Q${w / 2} ${h} ${w * 0.2} ${h * 0.95} Z`} fill="url(#crcG)" stroke="#0f172a" strokeWidth="0.6" />
      <ellipse cx={w / 2} cy={h * 0.2} rx={w * 0.35} ry={h * 0.06} fill="#f97316" />
      {running && <ellipse cx={w / 2} cy={h * 0.18} rx={w * 0.3} ry={h * 0.05} fill="#fef3c7"><animate attributeName="opacity" values="0.6;1;0.6" dur="0.9s" repeatCount="indefinite" /></ellipse>}
    </g>
  ),
  tongs: ({ w, h }) => (
    <g>
      <path d={`M0 ${h * 0.3} L${w * 0.4} ${h / 2} L${w * 0.4} ${h * 0.7} L0 ${h * 0.9}`} stroke="#334155" strokeWidth="2" fill="none" strokeLinecap="round" />
      <rect x={w * 0.4} y={h * 0.4} width={w * 0.6} height={h * 0.2} fill="#374151" stroke="#0f172a" strokeWidth="0.4" />
      <circle cx={w * 0.4} cy={h / 2} r="1" fill="#0f172a" />
    </g>
  ),

  // --- CIVIL ----------------------------------------------------------------
  foundation: ({ w, h }) => (
    <g>
      <defs>{grad('fdnG', '#78716c', '#292524')}</defs>
      <rect x="0" y="0" width={w} height={h} fill="url(#fdnG)" stroke="#0f172a" strokeWidth="0.5" />
      {[0.15, 0.35, 0.55, 0.75].map((f, i) => (
        <rect key={i} x={w * f} y="1" width="1" height={h - 2} fill="#292524" opacity="0.6" />
      ))}
      <path d={`M0 ${h * 0.3} L${w} ${h * 0.3}`} stroke="#292524" strokeWidth="0.3" strokeDasharray="2 2" />
    </g>
  ),
  column: ({ w, h }) => (
    <g>
      <defs>{grad('colG', '#e7e5e4', '#78716c', false)}</defs>
      <rect x="1" y="0" width={w - 2} height={h} fill="url(#colG)" stroke="#0f172a" strokeWidth="0.5" />
      <rect x="0" y="0" width={w} height={h * 0.05} fill="#78716c" />
      <rect x="0" y={h - h * 0.05} width={w} height={h * 0.05} fill="#78716c" />
    </g>
  ),
  beam: ({ w, h }) => (
    <g>
      <defs>{grad('bmG', '#e7e5e4', '#78716c')}</defs>
      <rect x="0" y="1" width={w} height={h - 2} fill="url(#bmG)" stroke="#0f172a" strokeWidth="0.5" />
      <rect x="0" y="1" width={w} height={h * 0.2} fill="white" opacity="0.4" />
      {[0.1, 0.3, 0.5, 0.7, 0.9].map((f, i) => (
        <circle key={i} cx={w * f} cy={h / 2} r="1" fill="#57534e" />
      ))}
    </g>
  ),
  slab: ({ w, h }) => (
    <g>
      <defs>{grad('slbG', '#f5f5f4', '#a8a29e')}</defs>
      <rect x="0" y="0" width={w} height={h} fill="url(#slbG)" stroke="#0f172a" strokeWidth="0.5" />
      <path d="M0 2 H100" stroke="#78716c" strokeWidth="0.2" />
    </g>
  ),
  wall: ({ w, h }) => (
    <g>
      <defs>
        <pattern id={`bkPat-${Math.round(w+h)}`} width="12" height="6" patternUnits="userSpaceOnUse">
          <rect width="12" height="6" fill="#dc2626" />
          <rect x="0" y="0" width="6" height="3" fill="#b91c1c" />
          <rect x="6" y="3" width="6" height="3" fill="#b91c1c" />
          <path d="M0 3 H12 M6 0 V3 M0 6 H12 M6 3 V6" stroke="#7f1d1d" strokeWidth="0.4" />
        </pattern>
      </defs>
      <rect x="0" y="0" width={w} height={h} fill={`url(#bkPat-${Math.round(w+h)})`} stroke="#0f172a" strokeWidth="0.5" />
    </g>
  ),
  door: ({ w, h }) => (
    <g>
      <defs>{grad('drG', '#b45309', '#78350f')}</defs>
      <rect x="1" y="1" width={w - 2} height={h - 2} rx="1" fill="url(#drG)" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.15} y={h * 0.15} width={w * 0.7} height={h * 0.35} rx="1" stroke="#78350f" strokeWidth="0.4" fill="none" />
      <rect x={w * 0.15} y={h * 0.55} width={w * 0.7} height={h * 0.3} rx="1" stroke="#78350f" strokeWidth="0.4" fill="none" />
      <circle cx={w * 0.85} cy={h * 0.55} r="1.2" fill="#facc15" stroke="#0f172a" strokeWidth="0.3" />
    </g>
  ),
  window_c: ({ w, h }) => (
    <g>
      <defs>{grad('winG', '#7dd3fc', '#0369a1')}</defs>
      <rect x="1" y="1" width={w - 2} height={h - 2} fill="url(#winG)" stroke="#78716c" strokeWidth="1" />
      <line x1={w / 2} y1="1" x2={w / 2} y2={h - 1} stroke="#e7e5e4" strokeWidth="0.8" />
      <line x1="1" y1={h / 2} x2={w - 1} y2={h / 2} stroke="#e7e5e4" strokeWidth="0.8" />
      <path d={`M2 2 L${w * 0.4} ${h * 0.4}`} stroke="white" strokeWidth="0.5" opacity="0.7" />
    </g>
  ),
  roof: ({ w, h }) => (
    <g>
      <defs>{grad('rfG', '#dc2626', '#7c2d12')}</defs>
      <path d={`M0 ${h} L${w * 0.15} ${h * 0.1} L${w * 0.85} ${h * 0.1} L${w} ${h} Z`} fill="url(#rfG)" stroke="#0f172a" strokeWidth="0.6" />
      {[0.1, 0.3, 0.5, 0.7, 0.9].map((f, i) => (
        <line key={i} x1={w * f} y1="0" x2={w * f + (f - 0.5) * 6} y2={h} stroke="#7f1d1d" strokeWidth="0.3" opacity="0.7" />
      ))}
    </g>
  ),
  stair: ({ w, h }) => (
    <g>
      <defs>{grad('stG', '#94a3b8', '#334155')}</defs>
      {[0, 0.15, 0.3, 0.45, 0.6, 0.75].map((f, i) => (
        <rect key={i} x={w * f} y={h - h * (0.15 + f)} width={w * 0.25} height={h * 0.15} fill="url(#stG)" stroke="#0f172a" strokeWidth="0.4" />
      ))}
    </g>
  ),
  rebar: ({ w, h }) => (
    <g>
      <rect x={w * 0.3} y="0" width={w * 0.4} height={h} fill="#dc2626" stroke="#0f172a" strokeWidth="0.4" />
      {[0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9].map((f, i) => (
        <rect key={i} x={w * 0.25} y={h * f - 0.4} width={w * 0.5} height="0.8" fill="#7f1d1d" />
      ))}
    </g>
  ),

  // --- CARPENTRY ------------------------------------------------------------
  plank: ({ w, h }) => (
    <g>
      <defs>{grad('plkG', '#c2410c', '#78350f')}</defs>
      <rect x="0" y="0" width={w} height={h} fill="url(#plkG)" stroke="#0f172a" strokeWidth="0.5" />
      <path d={`M2 ${h * 0.3} Q${w * 0.4} ${h * 0.15} ${w * 0.9} ${h * 0.3}`} stroke="#78350f" strokeWidth="0.4" fill="none" opacity="0.6" />
      <path d={`M2 ${h * 0.7} Q${w * 0.6} ${h * 0.85} ${w * 0.9} ${h * 0.7}`} stroke="#78350f" strokeWidth="0.4" fill="none" opacity="0.6" />
    </g>
  ),
  leg: ({ w, h }) => (
    <g>
      <defs>{grad('lgG', '#a16207', '#3f1503', false)}</defs>
      <rect x="1" y="0" width={w - 2} height={h} fill="url(#lgG)" stroke="#0f172a" strokeWidth="0.4" />
      <rect x="1" y={h * 0.15} width={w - 2} height="2" fill="#78350f" />
      <rect x="1" y={h * 0.85} width={w - 2} height="2" fill="#78350f" />
    </g>
  ),
  top: ({ w, h }) => (
    <g>
      <defs>{grad('tpG', '#d97706', '#78350f')}</defs>
      <rect x="0" y="1" width={w} height={h - 2} rx="2" fill="url(#tpG)" stroke="#0f172a" strokeWidth="0.5" />
      <ellipse cx={w * 0.25} cy={h * 0.4} rx={w * 0.2} ry={h * 0.15} fill="#78350f" opacity="0.4" />
      <path d={`M2 ${h / 2} Q${w * 0.5} ${h * 0.35} ${w - 2} ${h / 2}`} stroke="#78350f" strokeWidth="0.3" fill="none" opacity="0.5" />
    </g>
  ),
  chair_back: ({ w, h }) => (
    <g>
      <defs>{grad('cbG', '#b45309', '#78350f', false)}</defs>
      <rect x="0" y="0" width={w} height={h} fill="url(#cbG)" stroke="#0f172a" strokeWidth="0.4" />
      <rect x="1" y={h * 0.2} width={w - 2} height="1" fill="#78350f" />
      <rect x="1" y={h * 0.5} width={w - 2} height="1" fill="#78350f" />
      <rect x="1" y={h * 0.8} width={w - 2} height="1" fill="#78350f" />
    </g>
  ),
  nail: ({ w, h }) => (
    <g>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="#525252" stroke="#0f172a" strokeWidth="0.5" />
      <circle cx={w * 0.4} cy={h * 0.4} r={Math.min(w, h) * 0.15} fill="white" opacity="0.6" />
    </g>
  ),
  screw: ({ w, h }) => (
    <g>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="#71717a" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.2} y={h * 0.45} width={w * 0.6} height={h * 0.1} fill="#0f172a" />
      <rect x={w * 0.45} y={h * 0.2} width={w * 0.1} height={h * 0.6} fill="#0f172a" />
    </g>
  ),
  hinge: ({ w, h }) => (
    <g>
      <rect x="0" y="0" width={w * 0.4} height={h} fill="#71717a" stroke="#0f172a" strokeWidth="0.4" />
      <rect x={w * 0.6} y="0" width={w * 0.4} height={h} fill="#71717a" stroke="#0f172a" strokeWidth="0.4" />
      <rect x={w * 0.4} y="0" width={w * 0.2} height={h} fill="#334155" />
      {[0.15, 0.4, 0.65, 0.9].map((f, i) => (
        <circle key={i} cx={w / 2} cy={h * f} r="1" fill="#0f172a" />
      ))}
    </g>
  ),
  saw: ({ w, h }) => (
    <g>
      <rect x="0" y={h * 0.2} width={w * 0.75} height={h * 0.35} fill="#e2e8f0" stroke="#0f172a" strokeWidth="0.4" />
      <path d={`M0 ${h * 0.55} L${w * 0.05} ${h * 0.72} L${w * 0.1} ${h * 0.55} L${w * 0.15} ${h * 0.72} L${w * 0.2} ${h * 0.55} L${w * 0.25} ${h * 0.72} L${w * 0.3} ${h * 0.55} L${w * 0.35} ${h * 0.72} L${w * 0.4} ${h * 0.55} L${w * 0.45} ${h * 0.72} L${w * 0.5} ${h * 0.55} L${w * 0.55} ${h * 0.72} L${w * 0.6} ${h * 0.55} L${w * 0.65} ${h * 0.72} L${w * 0.7} ${h * 0.55} Z`} fill="#94a3b8" stroke="#0f172a" strokeWidth="0.4" />
      <rect x={w * 0.72} y={h * 0.1} width={w * 0.25} height={h * 0.55} rx="4" fill="#f59e0b" stroke="#0f172a" strokeWidth="0.4" />
    </g>
  ),
  chisel: ({ w, h }) => (
    <g>
      <rect x="0" y={h * 0.3} width={w * 0.5} height={h * 0.4} fill="#dc2626" stroke="#0f172a" strokeWidth="0.4" />
      <path d={`M${w * 0.5} ${h * 0.3} L${w} ${h / 2} L${w * 0.5} ${h * 0.7} Z`} fill="#e2e8f0" stroke="#0f172a" strokeWidth="0.4" />
    </g>
  ),
  drawer: ({ w, h }) => (
    <g>
      <defs>{grad('drwG', '#d97706', '#78350f')}</defs>
      <rect x="0" y="0" width={w} height={h} rx="2" fill="url(#drwG)" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.4} y={h * 0.35} width={w * 0.2} height={h * 0.3} rx="1" fill="#facc15" stroke="#0f172a" strokeWidth="0.4" />
      <rect x="1" y={h * 0.15} width={w - 2} height="0.8" fill="#78350f" opacity="0.7" />
      <rect x="1" y={h * 0.85} width={w - 2} height="0.8" fill="#78350f" opacity="0.7" />
    </g>
  ),

  // --- WELDING --------------------------------------------------------------
  plate: ({ w, h }) => (
    <g>
      <defs>{grad('pltG', '#94a3b8', '#334155')}</defs>
      <rect x="1" y="1" width={w - 2} height={h - 2} fill="url(#pltG)" stroke="#0f172a" strokeWidth="0.6" />
      <rect x="1" y="1" width={w - 2} height={h * 0.25} fill="white" opacity="0.35" />
      {[0.15, 0.5, 0.85].map((f, i) => (
        <circle key={i} cx={w * f} cy={h * 0.5} r="0.8" fill="#0f172a" opacity="0.6" />
      ))}
    </g>
  ),
  torch: ({ w, h, running }) => (
    <g>
      <defs>
        <linearGradient id="trchG" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#ea580c" />
          <stop offset="1" stopColor="#7c2d12" />
        </linearGradient>
        <radialGradient id="flameG" cx="0.7" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#fef3c7" />
          <stop offset="0.5" stopColor="#f59e0b" />
          <stop offset="1" stopColor="#7c2d12" stopOpacity="0.4" />
        </radialGradient>
      </defs>
      <rect x="0" y={h * 0.3} width={w * 0.6} height={h * 0.4} rx="4" fill="url(#trchG)" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.55} y={h * 0.42} width={w * 0.15} height={h * 0.16} fill="#334155" />
      {running ? (
        <ellipse cx={w * 0.85} cy={h * 0.5} rx={w * 0.2} ry={h * 0.28} fill="url(#flameG)">
          <animate attributeName="rx" values={`${w*0.18};${w*0.24};${w*0.18}`} dur="0.5s" repeatCount="indefinite" />
        </ellipse>
      ) : (
        <circle cx={w * 0.85} cy={h * 0.5} r={w * 0.06} fill="#334155" />
      )}
    </g>
  ),
  electrode: ({ w, h }) => (
    <g>
      <rect x={w * 0.3} y="0" width={w * 0.4} height={h * 0.15} fill="#71717a" />
      <rect x={w * 0.35} y={h * 0.15} width={w * 0.3} height={h * 0.85} fill="#facc15" stroke="#0f172a" strokeWidth="0.4" />
      <rect x={w * 0.42} y={h * 0.15} width={w * 0.05} height={h * 0.85} fill="white" opacity="0.5" />
    </g>
  ),
  gas_cyl: ({ w, h, running }) => (
    <g>
      <defs>{grad('gasG', '#f87171', '#7f1d1d', false)}</defs>
      <rect x="1" y={h * 0.12} width={w - 2} height={h * 0.88} rx={w * 0.15} fill="url(#gasG)" stroke="#0f172a" strokeWidth="0.6" />
      <rect x={w * 0.35} y="0" width={w * 0.3} height={h * 0.12} fill="#525252" rx="1" />
      <rect x={w * 0.3} y={h * 0.12} width={w * 0.4} height="2" fill="#334155" />
      <circle cx={w / 2} cy={h * 0.06} r={w * 0.12} fill="#facc15" />
      <text x={w * 0.25} y={h * 0.55} fill="white" fontSize={h * 0.15} fontWeight="900">O₂</text>
      {running && <circle cx={w / 2} cy={h * 0.06} r={w * 0.15} fill="none" stroke="#facc15" strokeWidth="0.6"><animate attributeName="r" values={`${w*0.1};${w*0.2}`} dur="1s" repeatCount="indefinite" /></circle>}
    </g>
  ),
  clamp: ({ w, h }) => (
    <g>
      <rect x="1" y={h * 0.2} width={w - 2} height={h * 0.6} fill="#334155" stroke="#0f172a" strokeWidth="0.5" />
      <rect x="0" y="1" width={w * 0.15} height={h - 2} fill="#0f172a" />
      <rect x={w - w * 0.15} y="1" width={w * 0.15} height={h - 2} fill="#0f172a" />
      <circle cx={w / 2} cy={h / 2} r="1.5" fill="#facc15" />
    </g>
  ),
  ground_cable: ({ w, h }) => (
    <g>
      <path d={`M0 ${h / 2} Q${w / 4} ${h * 0.2} ${w / 2} ${h / 2} T${w} ${h / 2}`} stroke="#22c55e" strokeWidth={h * 0.5} fill="none" strokeLinecap="round" />
      <path d={`M0 ${h / 2} Q${w / 4} ${h * 0.2} ${w / 2} ${h / 2} T${w} ${h / 2}`} stroke="#166534" strokeWidth={h * 0.15} fill="none" strokeLinecap="round" />
    </g>
  ),
  mask: ({ w, h }) => (
    <g>
      <defs>{grad('mskG', '#334155', '#0f172a')}</defs>
      <rect x="1" y="4" width={w - 2} height={h - 5} rx="4" fill="url(#mskG)" stroke="#0f172a" strokeWidth="0.6" />
      <rect x={w * 0.15} y={h * 0.25} width={w * 0.7} height={h * 0.3} fill="#0f172a" stroke="#22c55e" strokeWidth="0.5" />
      <rect x={w * 0.15} y={h * 0.25} width={w * 0.7} height={h * 0.15} fill="#166534" opacity="0.5" />
      <rect x={w * 0.4} y="0" width={w * 0.2} height="4" fill="#292524" />
    </g>
  ),
  bead: ({ w, h }) => (
    <g>
      <defs>{grad('bdG', '#fef3c7', '#a16207')}</defs>
      <path d={`M0 ${h / 2} Q${w * 0.1} 0 ${w * 0.2} ${h / 2} T${w * 0.4} ${h / 2} T${w * 0.6} ${h / 2} T${w * 0.8} ${h / 2} T${w} ${h / 2}`} stroke="url(#bdG)" strokeWidth={h * 0.8} fill="none" strokeLinecap="round" />
    </g>
  ),
  grinder: ({ w, h, running }) => (
    <g>
      <defs>{grad('gndG', '#a78bfa', '#4c1d95')}</defs>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="url(#gndG)" stroke="#0f172a" strokeWidth="0.6" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 3} fill="#e2e8f0" stroke="#0f172a" strokeWidth="0.4">
        {running && <animateTransform attributeName="transform" type="rotate" from={`0 ${w / 2} ${h / 2}`} to={`360 ${w / 2} ${h / 2}`} dur="0.3s" repeatCount="indefinite" />}
      </circle>
      <circle cx={w / 2} cy={h / 2} r="1.5" fill="#0f172a" />
    </g>
  ),
  filler: ({ w, h }) => (
    <g>
      <rect x={w * 0.35} y="0" width={w * 0.3} height={h} fill="#c2410c" stroke="#0f172a" strokeWidth="0.4" />
      <rect x={w * 0.4} y="0" width={w * 0.08} height={h} fill="#fed7aa" opacity="0.6" />
    </g>
  ),

  // --- FITTERS --------------------------------------------------------------
  bolt: ({ w, h }) => (
    <g>
      <defs>{grad('bltG', '#94a3b8', '#334155')}</defs>
      <polygon points={`${w * 0.5},${h * 0.05} ${w * 0.9},${h * 0.25} ${w * 0.9},${h * 0.55} ${w * 0.5},${h * 0.75} ${w * 0.1},${h * 0.55} ${w * 0.1},${h * 0.25}`} fill="url(#bltG)" stroke="#0f172a" strokeWidth="0.6" />
      <rect x={w * 0.42} y={h * 0.7} width={w * 0.16} height={h * 0.25} fill="#334155" />
    </g>
  ),
  nut: ({ w, h }) => (
    <g>
      <defs>{grad('ntG', '#94a3b8', '#334155')}</defs>
      <polygon points={`${w * 0.5},${h * 0.05} ${w * 0.9},${h * 0.28} ${w * 0.9},${h * 0.72} ${w * 0.5},${h * 0.95} ${w * 0.1},${h * 0.72} ${w * 0.1},${h * 0.28}`} fill="url(#ntG)" stroke="#0f172a" strokeWidth="0.6" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) * 0.22} fill="#0f172a" />
    </g>
  ),
  washer: ({ w, h }) => (
    <g>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="#cbd5e1" stroke="#0f172a" strokeWidth="0.6" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 4} fill="#0f172a" />
      <circle cx={w * 0.4} cy={h * 0.4} r="1" fill="white" opacity="0.6" />
    </g>
  ),
  spanner: ({ w, h }) => (
    <g>
      <defs>{grad('spnG', '#94a3b8', '#334155')}</defs>
      <rect x={w * 0.2} y={h * 0.4} width={w * 0.6} height={h * 0.2} fill="url(#spnG)" stroke="#0f172a" strokeWidth="0.5" />
      <path d={`M0 ${h / 2} L${w * 0.15} ${h * 0.15} L${w * 0.25} ${h * 0.25} L${w * 0.15} ${h * 0.4} L${w * 0.25} ${h / 2} L${w * 0.15} ${h * 0.6} L${w * 0.25} ${h * 0.75} L${w * 0.15} ${h * 0.85} Z`} fill="url(#spnG)" stroke="#0f172a" strokeWidth="0.5" />
      <path d={`M${w} ${h / 2} L${w * 0.85} ${h * 0.15} L${w * 0.75} ${h * 0.25} L${w * 0.85} ${h * 0.4} L${w * 0.75} ${h / 2} L${w * 0.85} ${h * 0.6} L${w * 0.75} ${h * 0.75} L${w * 0.85} ${h * 0.85} Z`} fill="url(#spnG)" stroke="#0f172a" strokeWidth="0.5" />
    </g>
  ),
  flange: ({ w, h }) => (
    <g>
      <defs>{grad('flgG', '#475569', '#0f172a')}</defs>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="url(#flgG)" stroke="#0f172a" strokeWidth="0.6" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 3} fill="#0f172a" />
      {[0, 60, 120, 180, 240, 300].map((a) => (
        <circle key={a} cx={w / 2 + Math.cos(a * Math.PI / 180) * Math.min(w, h) * 0.38} cy={h / 2 + Math.sin(a * Math.PI / 180) * Math.min(w, h) * 0.38} r={Math.min(w, h) * 0.06} fill="#0f172a" />
      ))}
    </g>
  ),
  gasket: ({ w, h }) => (
    <g>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="#f97316" stroke="#7c2d12" strokeWidth="0.8" strokeDasharray="2 1.5" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 4} fill="none" stroke="#7c2d12" strokeWidth="0.6" />
    </g>
  ),
  union: ({ w, h }) => (
    <g>
      <defs>{grad('unG', '#0891b2', '#164e63')}</defs>
      <rect x="1" y={h * 0.25} width={w - 2} height={h * 0.5} rx="2" fill="url(#unG)" stroke="#0f172a" strokeWidth="0.6" />
      <circle cx={w / 2} cy={h / 2} r={h * 0.28} fill="none" stroke="#0f172a" strokeWidth="0.5" />
      <line x1={w / 2} y1={h * 0.2} x2={w / 2} y2={h * 0.8} stroke="#0f172a" strokeWidth="0.3" />
    </g>
  ),
  reducer: ({ w, h }) => (
    <g>
      <defs>{grad('rdrG', '#3b82f6', '#1e3a8a')}</defs>
      <path d={`M0 ${h * 0.15} L${w * 0.4} ${h * 0.15} L${w} ${h * 0.35} L${w} ${h * 0.65} L${w * 0.4} ${h * 0.85} L0 ${h * 0.85} Z`} fill="url(#rdrG)" stroke="#0f172a" strokeWidth="0.5" />
      <path d={`M2 ${h * 0.3} L${w * 0.4} ${h * 0.3} L${w * 0.9} ${h * 0.4}`} stroke="white" strokeWidth="0.4" fill="none" opacity="0.5" />
    </g>
  ),
  coupling: ({ w, h }) => (
    <g>
      <defs>{grad('cplG', '#a78bfa', '#4c1d95')}</defs>
      <rect x="0" y={h * 0.2} width={w} height={h * 0.6} rx="3" fill="url(#cplG)" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={w * 0.15} y={h * 0.3} width={w * 0.02} height={h * 0.4} fill="#0f172a" />
      <rect x={w * 0.85} y={h * 0.3} width={w * 0.02} height={h * 0.4} fill="#0f172a" />
      <rect x="0" y={h * 0.4} width={w} height={h * 0.1} fill="white" opacity="0.3" />
    </g>
  ),
  threader: ({ w, h, running }) => (
    <g>
      <defs>{grad('thrG', '#dc2626', '#7f1d1d')}</defs>
      <rect x="1" y="1" width={w - 2} height={h - 2} rx="3" fill="url(#thrG)" stroke="#0f172a" strokeWidth="0.5" />
      <g transform={`translate(${w / 2} ${h / 2})`}>
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
          <rect key={a} x="-0.6" y={-Math.min(w, h) * 0.35} width="1.2" height={Math.min(w, h) * 0.7} fill="#94a3b8" transform={`rotate(${a})`} />
        ))}
        <circle r={Math.min(w, h) * 0.2} fill="#0f172a" />
        {running && <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="0.5s" repeatCount="indefinite" additive="sum" />}
      </g>
    </g>
  ),
};

// Fallback for any part we haven't hand-crafted — uses the palette colour +
// emoji so nothing breaks.
export const renderPartArt = (baseType, props) => {
  const fn = PART_ART[baseType];
  return fn ? fn(props) : null;
};

// -----------------------------------------------------------------------------
// PROMPT_LIBRARY — keyword-driven scene builder.  User types natural
// language ("empty kitchen", "add cabinet", "add sink", "clear")  and
// the parser dispatches a scene change and/or a furniture drop.  No LLM.
//
// Each SCENE entry sets the promptScene (renders the room backdrop from
// SCENES) and clears the canvas.  Each PART entry defines a large-scale
// installable "furniture" piece — sink, cabinet, toilet, bathtub, shower
// stall — that gets dropped onto the canvas as a normal draggable part.
// -----------------------------------------------------------------------------
export const PROMPT_LIBRARY = {
  scenes: [
    { match: /\b(empty|blank|clear|base)\s*(indian\s*)?kitchen\b/i,   scene: 'kitchen',  label: 'empty kitchen' },
    { match: /\b(empty|blank|clear|base)\s*bathroom\b/i,              scene: 'bathroom', label: 'empty bathroom' },
    { match: /\b(empty|blank|clear|base)\s*(garden|lawn|yard)\b/i,    scene: 'garden',   label: 'empty garden' },
    { match: /\b(empty|blank|clear|base)\s*(workshop|garage)\b/i,     scene: 'workshop', label: 'empty workshop' },
    { match: /\b(empty|blank|clear|base)\s*(factory|foundry|mill)\b/i,scene: 'factory',  label: 'empty factory' },
  ],
  // Furniture "installations" — pseudo-parts that fit the base scenes.
  // Reuse existing PART_ART keys where possible; add new ones for cabinet
  // / sink / toilet / bathtub / shower_stall / hood / stove below.
  parts: [
    { match: /\b(kitchen\s+)?cabinet(s|ry)?\b/i,     id: 'cabinet',       label: 'Cabinet',       emoji: '🗄️', w: 120, h: 90, colour: '#a16207' },
    { match: /\b(kitchen\s+)?sink\b/i,               id: 'sink_kitchen',  label: 'Kitchen Sink',  emoji: '🚰', w: 130, h: 60, colour: '#71717a' },
    { match: /\b(faucet|tap|mixer)\b/i,              id: 'tap',           label: 'Kitchen Tap',   emoji: '🚰', w: 44, h: 62, colour: '#94a3b8' },
    { match: /\b(toilet|commode|wc)\b/i,             id: 'toilet',        label: 'Toilet',        emoji: '🚽', w: 84, h: 100, colour: '#f8fafc' },
    { match: /\b(bathtub|tub)\b/i,                   id: 'bathtub',       label: 'Bathtub',       emoji: '🛁', w: 180, h: 88, colour: '#e0f2fe' },
    { match: /\b(shower(\s+stall)?)\b/i,             id: 'shower_stall',  label: 'Shower Stall',  emoji: '🚿', w: 110, h: 160, colour: '#7dd3fc' },
    { match: /\b(stove|cooktop|range)\b/i,           id: 'stove',         label: 'Stove',         emoji: '🍳', w: 110, h: 66, colour: '#1f2937' },
    { match: /\b(hood|chimney|vent)\b/i,             id: 'hood',          label: 'Chimney Hood',  emoji: '🍽️', w: 130, h: 46, colour: '#374151' },
    { match: /\b(fridge|refrigerator)\b/i,           id: 'fridge',        label: 'Fridge',        emoji: '🧊', w: 90, h: 170, colour: '#e5e7eb' },
    { match: /\b(pipe(s)?|plumbing\s+line)\b/i,      id: 'pipe_h',        label: 'Straight Pipe', emoji: '│', w: 100, h: 20, colour: '#7dd3fc' },
    { match: /\b(valve|shutoff)\b/i,                 id: 'valve',         label: 'Ball Valve',    emoji: '⏹️', w: 48, h: 48, colour: '#f87171' },
    { match: /\b(drain|floor\s*drain)\b/i,           id: 'drain',         label: 'Floor Drain',   emoji: '⚫', w: 36, h: 36, colour: '#475569' },
    { match: /\b(tank|water\s*tank|cistern)\b/i,     id: 'tank',          label: 'Water Tank',    emoji: '💧', w: 90, h: 110, colour: '#60a5fa' },
    { match: /\b(pump|booster)\b/i,                  id: 'pump',          label: 'Booster Pump',  emoji: '⚙️', w: 60, h: 60, colour: '#34d399' },
    { match: /\b(garden\s*bed|flower\s*bed|planter)\b/i, id: 'garden_bed',id: 'garden_bed',   label: 'Garden Bed',    emoji: '🌱', w: 120, h: 40, colour: '#84cc16' },
    { match: /\b(sprinkler|drip)\b/i,                id: 'sprinkler',     label: 'Sprinkler',     emoji: '💦', w: 40, h: 40, colour: '#38bdf8' },
    { match: /\b(workbench|bench|table)\b/i,         id: 'workbench',     label: 'Workbench',     emoji: '🪑', w: 160, h: 44, colour: '#78350f' },
  ],
  commands: [
    { match: /\b(clear|reset|wipe|remove\s+all)\b/i, action: 'clear' },
    { match: /\b(run|start|simulate)\b/i,            action: 'run' },
  ],
};

// New PART_ART entries for prompt-driven furniture.  Detailed enough to
// look like real installations without external images.
Object.assign(PART_ART, {
  cabinet: ({ w, h }) => (
    <g>
      <defs>
        <linearGradient id={`cabG-${w}-${h}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#b45309" />
          <stop offset="1" stopColor="#78350f" />
        </linearGradient>
      </defs>
      <rect x="1" y="6" width={w - 2} height={h - 8} rx="2" fill={`url(#cabG-${w}-${h})`} stroke="#3f1503" strokeWidth="0.8" />
      <rect x="1" y="1" width={w - 2} height="6" fill="#292524" />
      <rect x="1" y={h - 2} width={w - 2} height="2" fill="#292524" />
      {/* Two doors */}
      <rect x={w * 0.05} y={h * 0.15} width={w * 0.42} height={h * 0.75} rx="1" fill="none" stroke="#3f1503" strokeWidth="0.8" />
      <rect x={w * 0.53} y={h * 0.15} width={w * 0.42} height={h * 0.75} rx="1" fill="none" stroke="#3f1503" strokeWidth="0.8" />
      {/* Wood grain */}
      <path d={`M ${w * 0.1} ${h * 0.35} Q ${w * 0.25} ${h * 0.3} ${w * 0.4} ${h * 0.35}`} stroke="#78350f" strokeWidth="0.4" fill="none" opacity="0.55" />
      <path d={`M ${w * 0.58} ${h * 0.55} Q ${w * 0.75} ${h * 0.5} ${w * 0.9} ${h * 0.55}`} stroke="#78350f" strokeWidth="0.4" fill="none" opacity="0.55" />
      {/* Handles */}
      <circle cx={w * 0.4} cy={h / 2} r={h * 0.03} fill="#fbbf24" stroke="#78350f" strokeWidth="0.2" />
      <circle cx={w * 0.6} cy={h / 2} r={h * 0.03} fill="#fbbf24" stroke="#78350f" strokeWidth="0.2" />
    </g>
  ),
  sink_kitchen: ({ w, h }) => (
    <g>
      <defs>
        <linearGradient id={`skG-${w}-${h}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#71717a" />
          <stop offset="1" stopColor="#18181b" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width={w} height={h * 0.15} fill="#e5e7eb" />
      <rect x="1" y={h * 0.15} width={w - 2} height={h * 0.75} rx="2" fill={`url(#skG-${w}-${h})`} stroke="#0f172a" strokeWidth="0.6" />
      <rect x={w * 0.485} y={h * 0.18} width="1" height={h * 0.7} fill="#111827" />
      <circle cx={w * 0.24} cy={h * 0.5} r={h * 0.09} fill="#020617" />
      <circle cx={w * 0.76} cy={h * 0.5} r={h * 0.09} fill="#020617" />
      <rect x="0" y={h * 0.9} width={w} height={h * 0.1} fill="#a16207" />
    </g>
  ),
  toilet: ({ w, h }) => (
    <g>
      <defs>
        <linearGradient id={`tlG-${w}-${h}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f8fafc" />
          <stop offset="1" stopColor="#94a3b8" />
        </linearGradient>
      </defs>
      {/* Tank */}
      <rect x={w * 0.15} y="0" width={w * 0.7} height={h * 0.35} rx="1.5" fill={`url(#tlG-${w}-${h})`} stroke="#334155" strokeWidth="0.5" />
      <circle cx={w * 0.75} cy={h * 0.1} r={h * 0.03} fill="#94a3b8" />
      {/* Bowl */}
      <ellipse cx={w / 2} cy={h * 0.65} rx={w * 0.45} ry={h * 0.28} fill={`url(#tlG-${w}-${h})`} stroke="#334155" strokeWidth="0.5" />
      <ellipse cx={w / 2} cy={h * 0.65} rx={w * 0.32} ry={h * 0.18} fill="#7dd3fc" opacity="0.7" />
      <rect x={w * 0.15} y={h * 0.9} width={w * 0.7} height={h * 0.1} fill="#94a3b8" />
    </g>
  ),
  bathtub: ({ w, h }) => (
    <g>
      <defs>
        <linearGradient id={`btG-${w}-${h}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f8fafc" />
          <stop offset="1" stopColor="#cbd5e1" />
        </linearGradient>
      </defs>
      <path d={`M 0 ${h * 0.15} L ${w * 0.15} 0 L ${w * 0.85} 0 L ${w} ${h * 0.15} L ${w * 0.95} ${h * 0.9} Q ${w / 2} ${h} ${w * 0.05} ${h * 0.9} Z`} fill={`url(#btG-${w}-${h})`} stroke="#334155" strokeWidth="0.8" />
      <ellipse cx={w / 2} cy={h * 0.5} rx={w * 0.4} ry={h * 0.32} fill="#7dd3fc" opacity="0.55" stroke="#0369a1" strokeWidth="0.35" />
      {/* faucet */}
      <rect x={w * 0.05} y={h * 0.35} width={w * 0.1} height={h * 0.06} fill="#94a3b8" rx="1" />
      <circle cx={w * 0.06} cy={h * 0.38} r={h * 0.03} fill="#dc2626" />
      <circle cx={w * 0.14} cy={h * 0.38} r={h * 0.03} fill="#3b82f6" />
    </g>
  ),
  shower_stall: ({ w, h }) => (
    <g>
      <defs>
        <linearGradient id={`ssG-${w}-${h}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f0f9ff" />
          <stop offset="1" stopColor="#7dd3fc" />
        </linearGradient>
      </defs>
      {/* stall glass */}
      <rect x="1" y="1" width={w - 2} height={h - 2} fill={`url(#ssG-${w}-${h})`} opacity="0.5" stroke="#0284c7" strokeWidth="1.2" rx="1" />
      {/* shower head arm + head */}
      <rect x={w * 0.45} y={h * 0.03} width={w * 0.1} height={h * 0.05} fill="#94a3b8" />
      <rect x={w * 0.35} y={h * 0.08} width={w * 0.3} height={h * 0.04} rx="1" fill="#334155" />
      {/* water lines */}
      {[0.4, 0.5, 0.6].map((f, i) => (
        <line key={i} x1={w * f} y1={h * 0.12} x2={w * f} y2={h * 0.4} stroke="#38bdf8" strokeWidth="0.6" opacity="0.7" />
      ))}
      {/* handle */}
      <circle cx={w * 0.15} cy={h * 0.4} r={h * 0.03} fill="#dc2626" />
      {/* drain */}
      <circle cx={w / 2} cy={h - h * 0.06} r={h * 0.04} fill="#0f172a" />
    </g>
  ),
  stove: ({ w, h }) => (
    <g>
      <rect x="1" y="1" width={w - 2} height={h - 2} rx="2" fill="#1f2937" stroke="#0f172a" strokeWidth="0.6" />
      {[0.25, 0.75].map((fx, i) => [0.35, 0.75].map((fy, j) => (
        <g key={`${i}-${j}`}>
          <circle cx={w * fx} cy={h * fy} r={Math.min(w, h) * 0.11} fill="#0f172a" stroke="#57534e" strokeWidth="0.4" />
          <circle cx={w * fx} cy={h * fy} r={Math.min(w, h) * 0.06} fill="#dc2626" opacity="0.8" />
        </g>
      )))}
      <rect x="4" y="4" width={w - 8} height="6" fill="#111827" />
      {[0.2, 0.4, 0.6, 0.8].map((f, i) => (
        <circle key={i} cx={w * f} cy="7" r="1.2" fill="#f59e0b" />
      ))}
    </g>
  ),
  hood: ({ w, h }) => (
    <g>
      <path d={`M 0 ${h * 0.7} L ${w * 0.15} ${h * 0.05} L ${w * 0.85} ${h * 0.05} L ${w} ${h * 0.7} L ${w} ${h} L 0 ${h} Z`} fill="#374151" stroke="#0f172a" strokeWidth="0.6" />
      <rect x="0" y={h * 0.65} width={w} height={h * 0.15} fill="#111827" />
      <rect x={w * 0.3} y={h * 0.8} width={w * 0.4} height={h * 0.05} fill="#f59e0b" opacity="0.7" />
    </g>
  ),
  fridge: ({ w, h }) => (
    <g>
      <defs>
        <linearGradient id={`frG-${w}-${h}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f8fafc" />
          <stop offset="1" stopColor="#cbd5e1" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width={w - 2} height={h - 2} rx="2" fill={`url(#frG-${w}-${h})`} stroke="#334155" strokeWidth="0.8" />
      <line x1="1" y1={h * 0.35} x2={w - 1} y2={h * 0.35} stroke="#334155" strokeWidth="0.6" />
      <rect x={w - 8} y={h * 0.1} width="4" height="12" rx="1" fill="#334155" />
      <rect x={w - 8} y={h * 0.42} width="4" height="18" rx="1" fill="#334155" />
    </g>
  ),
  garden_bed: ({ w, h }) => (
    <g>
      <rect x="1" y={h * 0.4} width={w - 2} height={h * 0.6} fill="#78350f" stroke="#3f1503" strokeWidth="0.6" rx="1" />
      {/* soil */}
      <rect x="2" y={h * 0.45} width={w - 4} height={h * 0.5} fill="#57342b" />
      {/* plants */}
      {[0.15, 0.35, 0.55, 0.75].map((f, i) => (
        <g key={i} transform={`translate(${w * f} ${h * 0.45})`}>
          <rect x="-0.4" y="0" width="0.8" height={h * 0.15} fill="#166534" />
          <path d={`M 0 0 L -3 -5 L 0 -8 L 3 -5 Z`} fill="#22c55e" />
        </g>
      ))}
    </g>
  ),
  sprinkler: ({ w, h, running }) => (
    <g>
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 2 - 1} fill="#38bdf8" stroke="#0369a1" strokeWidth="0.6" />
      <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) / 4} fill="#0f172a" />
      {running && [0, 60, 120, 180, 240, 300].map((a) => (
        <line key={a} x1={w / 2} y1={h / 2} x2={w / 2 + Math.cos(a * Math.PI / 180) * w * 0.6} y2={h / 2 + Math.sin(a * Math.PI / 180) * w * 0.6} stroke="#7dd3fc" strokeWidth="0.5" opacity="0.75" strokeDasharray="1 2" />
      ))}
    </g>
  ),
  workbench: ({ w, h }) => (
    <g>
      <rect x="0" y="0" width={w} height={h * 0.6} fill="#a16207" stroke="#3f1503" strokeWidth="0.5" />
      <rect x="0" y={h * 0.6} width={w} height={h * 0.05} fill="#3f1503" />
      <rect x={w * 0.05} y={h * 0.65} width={w * 0.06} height={h * 0.35} fill="#78350f" />
      <rect x={w * 0.89} y={h * 0.65} width={w * 0.06} height={h * 0.35} fill="#78350f" />
    </g>
  ),
});


// -----------------------------------------------------------------------------
// PART_SPECS — realistic engineering specifications per part type. Used for
// hover callouts so students see what a real plumber / electrician / welder
// would specify on a blueprint.
// -----------------------------------------------------------------------------
export const PART_SPECS = {
  // Plumbing
  tank:     { title: 'Water Tank',        spec: '500L HDPE · 4-inch inlet · 60 psi max' },
  pipe_h:   { title: 'Straight Pipe',     spec: '3/4-inch copper · 60 psi · L-type' },
  pipe_v:   { title: 'Vertical Pipe',     spec: '3/4-inch copper · sweat-soldered' },
  elbow:    { title: 'Elbow Joint',       spec: '90° · 3/4-inch copper · brazed' },
  tee:      { title: 'T-Joint',           spec: 'Tee splitter · 3/4-inch copper' },
  valve:    { title: 'Ball Valve',        spec: '1/2-inch brass · full-port · 600 WOG' },
  tap:      { title: 'Kitchen Tap',       spec: 'Single-lever mixer · 0.5 GPM aerator' },
  pump:     { title: 'Booster Pump',      spec: '0.5 HP · 30 psi boost · 12 LPM' },
  drain:    { title: 'Floor Drain',       spec: '2-inch PVC trap · 4-inch grate' },
  shower:   { title: 'Shower Head',       spec: '5-mode · 2.5 GPM · chrome' },

  // Electrical
  battery:  { title: 'Battery',           spec: '9V DC · 500 mAh · alkaline' },
  led:      { title: 'LED',               spec: '3V forward · 20 mA · 60mcd' },
  resistor: { title: 'Resistor',          spec: '220 Ω · 1/4 W · ±5% tolerance' },
  switch:   { title: 'SPST Switch',       spec: '250V AC · 5A · toggle-style' },
  motor:    { title: 'DC Motor',          spec: '6V · 300 RPM · 100 mA no-load' },
  capacitor:{ title: 'Capacitor',         spec: '100 µF · 25V electrolytic' },
  buzzer:   { title: 'Piezo Buzzer',      spec: '5V · 85 dB · 2.3 kHz' },
  ground:   { title: 'Ground',            spec: 'Earth reference · 0V rail' },
  sensor:   { title: 'Photocell',         spec: 'LDR · 10k-100k Ω dark/light' },
  wire:     { title: 'Wire Node',         spec: '22 AWG · copper stranded' },

  // Robotics
  chassis:  { title: 'Chassis',           spec: 'Aluminium · 140×95mm · 4WD-ready' },
  wheel:    { title: 'Wheel',             spec: '65mm rubber · IR-encoder ready' },
  motor2:   { title: 'Servo Motor',       spec: 'SG90 · 180° · 1.8 kg·cm torque' },
  sensor2:  { title: 'Ultrasonic Sensor', spec: 'HC-SR04 · 2-400 cm range' },
  arm:      { title: 'Robotic Arm',       spec: '3-DOF · 300g payload' },
  gripper:  { title: 'Gripper',           spec: 'Servo · 4cm max open' },
  camera:   { title: 'Camera Module',     spec: 'OV5647 · 5MP · 30 fps' },
  mcu:      { title: 'MCU',               spec: 'ATmega328P · 16 MHz · 32KB flash' },
  battery2: { title: 'Battery Pack',      spec: '2S LiPo · 7.4V · 2200 mAh' },
  led2:     { title: 'Status LED',        spec: '3mm indicator · 3V · 20 mA' },

  // Mining
  ore_gold:  { title: 'Gold Ore',         spec: 'Au grade 8 g/t · quartz vein' },
  ore_iron:  { title: 'Iron Ore',         spec: 'Fe₂O₃ hematite · 62% Fe content' },
  ore_coal:  { title: 'Coal Seam',        spec: 'Bituminous · 6800 kcal/kg' },
  drill:     { title: 'Rotary Drill',     spec: 'DTH bit · 150mm hole · 30 m/hr' },
  excavator: { title: 'Excavator',        spec: '5 m³ bucket · 40 t class' },
  truck:     { title: 'Haul Truck',       spec: '100-ton payload · 4200 hp' },
  conveyor:  { title: 'Conveyor Belt',    spec: '800mm belt · 2 m/s · 500 tph' },
  crusher:   { title: 'Jaw Crusher',      spec: '900×1200mm · 250 kW · 400 tph' },
  helmet:    { title: 'Safety Helmet',    spec: 'PPE Class E · ANSI Z89.1' },
  shaft:     { title: 'Mine Shaft',       spec: '6m diameter · lined · 200m deep' },

  // Metallurgy
  furnace:      { title: 'Blast Furnace', spec: '1550°C · 3200 m³ · continuous' },
  ladle:        { title: 'Ladle',         spec: '50L teapot · 1600°C molten' },
  ingot:        { title: 'Ingot',         spec: 'Fe · 60×26mm cast · 4.5 kg' },
  roller:       { title: 'Rolling Mill',  spec: 'Hot-rolling · 800°C · 2 m/min' },
  quench:       { title: 'Quench Tank',   spec: 'Water/oil · 100 L/min flow' },
  alloy_brass:  { title: 'Brass Alloy',   spec: 'Cu-Zn · 70/30 · 930°C melt' },
  alloy_bronze: { title: 'Bronze Alloy',  spec: 'Cu-Sn · 88/12 · 950°C melt' },
  anvil:        { title: 'Anvil',         spec: 'Cast steel · 120 lbs · hardened' },
  crucible:     { title: 'Crucible',      spec: 'Graphite · 5 kg capacity' },
  tongs:        { title: 'Forge Tongs',   spec: 'Steel · 600mm · V-jaw' },

  // Civil
  foundation: { title: 'Foundation',      spec: 'RCC · M25 · 1.2m depth' },
  column:     { title: 'RCC Column',      spec: '300×300mm · Fe500 rebar' },
  beam:       { title: 'RCC Beam',        spec: '300×450mm · 4-#20 bars' },
  slab:       { title: 'RCC Slab',        spec: '150mm thick · #12 @ 200 c/c' },
  wall:       { title: 'Brick Wall',      spec: '230mm · Class-A · 3.5 N/mm²' },
  door:       { title: 'Door',            spec: 'Teak · 2100×900×35mm' },
  window_c:   { title: 'Window',          spec: 'UPVC · double-glazed · 5mm' },
  roof:       { title: 'Sloped Roof',     spec: 'Metal seam · 25° pitch' },
  stair:      { title: 'Staircase',       spec: 'Riser 175mm · Tread 275mm' },
  rebar:      { title: 'Rebar',           spec: 'Fe500 · 12mm · deformed' },

  // Carpentry
  plank:      { title: 'Wood Plank',      spec: 'Pine · 1200×150×25mm · KD' },
  leg:        { title: 'Table Leg',       spec: 'Teak · 720mm · turned' },
  top:        { title: 'Table Top',       spec: 'Oak · 1800×900×40mm' },
  chair_back: { title: 'Chair Back',      spec: 'Beech laminate · 400mm' },
  nail:       { title: 'Wire Nail',       spec: '2.5×50mm · galvanised' },
  screw:      { title: 'Wood Screw',      spec: '#8 × 40mm · Phillips head' },
  hinge:      { title: 'Butt Hinge',      spec: '75mm brass · 3-pin' },
  saw:        { title: 'Handsaw',         spec: '22-inch · 8 TPI crosscut' },
  chisel:     { title: 'Chisel',          spec: '25mm bevel edge · Cr-V steel' },
  drawer:     { title: 'Drawer',          spec: 'Ball-bearing runners · 45cm' },

  // Welding
  plate:        { title: 'Steel Plate',   spec: 'A36 · 6mm × 200×200mm' },
  torch:        { title: 'MIG Torch',     spec: '250A · 0.9mm wire feed' },
  electrode:    { title: 'Electrode',     spec: 'E7018 · 3.2mm · low-hydrogen' },
  gas_cyl:      { title: 'Gas Cylinder',  spec: 'Argon/CO₂ mix · 200 bar · 47L' },
  clamp:        { title: 'C-Clamp',       spec: '6-inch drop-forged · 1.5 t' },
  ground_cable: { title: 'Ground Cable',  spec: '35 mm² · 500A · 3m length' },
  mask:         { title: 'Welder Mask',   spec: 'Auto-darkening · Shade 9-13' },
  bead:         { title: 'Weld Bead',     spec: '6mm fillet · single pass' },
  grinder:      { title: 'Angle Grinder', spec: '125mm disc · 900W · 11000 rpm' },
  filler:       { title: 'Filler Rod',    spec: 'ER70S-6 · 3.2mm · 5 kg spool' },

  // Fitters
  bolt:      { title: 'Hex Bolt',         spec: 'M10 × 30 · Grade 8.8 · zinc' },
  nut:       { title: 'Hex Nut',          spec: 'M10 · Grade 8 · zinc-plated' },
  washer:    { title: 'Flat Washer',      spec: 'M10 · 20mm OD · Grade A2-70' },
  spanner:   { title: 'Spanner',          spec: 'Combination · 10mm · Cr-V' },
  flange:    { title: 'Pipe Flange',      spec: 'ANSI 150# · 3-inch · slip-on' },
  gasket:    { title: 'Gasket',           spec: 'Spiral wound · 3-inch · SS316' },
  union:     { title: 'Union Joint',      spec: 'Screwed · 3/4-inch BSP' },
  reducer:   { title: 'Pipe Reducer',     spec: 'Concentric · 2×1-inch BSP' },
  coupling:  { title: 'Coupling',         spec: 'Socket · 3/4-inch schedule 40' },
  threader:  { title: 'Pipe Threader',    spec: 'Ratchet · 1/2 to 2-inch BSP' },
};

// Prompt-driven furniture specs — merged after PART_SPECS declaration
Object.assign(PART_SPECS, {
  cabinet:       { title: 'Kitchen Cabinet',  spec: 'MDF · 900×600×720mm · 2-door' },
  sink_kitchen:  { title: 'Kitchen Sink',     spec: 'SS304 double-bowl · 32×18-inch' },
  toilet:        { title: 'Toilet · Commode', spec: 'Wall-hung · 6L dual-flush' },
  bathtub:       { title: 'Bathtub',          spec: 'Acrylic · 1700×750mm · 180L' },
  shower_stall:  { title: 'Shower Stall',     spec: 'Toughened glass · 900×900mm' },
  stove:         { title: 'Gas Stove',        spec: '4-burner · SS body · piezo ignition' },
  hood:          { title: 'Chimney Hood',     spec: 'Auto-clean · 1200 m³/hr · 60cm' },
  fridge:        { title: 'Refrigerator',     spec: 'Double-door · 265L · 3-star' },
  garden_bed:    { title: 'Garden Bed',       spec: 'Timber raised · 1.2×0.4m' },
  sprinkler:     { title: 'Sprinkler',        spec: 'Pop-up · 360° · 5m radius' },
  workbench:     { title: 'Workbench',        spec: 'Wooden · 1500×600×850mm' },
});
